import { animate, inView, scroll, stagger, frame, cancelFrame, hover, wrap, resize, motionValue, styleEffect, transformValue, svgEffect, springValue } from "@theme/m";
import { $4, $$4, $id4, matchMediaQuery, currency, createImg, renderSrc } from "@theme/utilities";
import { Modal, Popover, Drawer, getterAdd, getterGet, isRTL } from "@theme/global";
import { ThemeEvents } from '@theme/events';
const { dialogAdded, dialogOpening, dialogOpen, dialogClosing, dialogClose, loadingStart, loadingEnd, cartUpdate, cartReload, admEvts } = ThemeEvents;
import { computePosition, shift, flip, offset, arrow } from "@theme/floating";

// $4 --> singular selector
// $$4 --> plural selector
// $id4 --> id selector

// $4('.aa') == document.querySelector('.aa')
// $4('.aa', section) == section.querySelector('.aa')

// $$(.aa) == document.querySelectorAll('.aa')
// $$(.aa, section) == section.querySelectorAll('.aa')

// $id4('aa') == document.getElementById('aa')

// https://motion.dev/docs/animate
// https://motion.dev/docs/inview
// https://motion.dev/docs/scroll

// Reveal Elements On Scroll
class HdtRevealElementsOnScroll {
    constructor(container, options = {}) {
        this.container = container;
        this.isRevealVertical = false;
        this.elems = $$4('[reveal], [can-reveal]', this.container).filter(elem => !this.isInsideSliderReveal(elem));
        this.defaultDir = this.container.getAttribute('default-reveal') || (isRTL ? 'right' : 'left');
        this.options = Object.assign({
            duration: 0.48,
            delay: 0.09,
            distance: '16px',
            ease: 'easeOut',
            once: true,
            direction: this.defaultDir
        }, options);

        const attrDistance = container.getAttribute('reveal-distance');
        if (attrDistance && !isNaN(parseFloat(attrDistance))) {
            this.distance = attrDistance;
        } else {
            this.distance = this.options.distance; // fallback
        }

        if (container.hasAttribute('reveal-repeat')) {
            this.options.once = false;
        }
        this.init();
    }

    isInsideSliderReveal(elem) {
      let parent = elem.parentElement;
      while (parent) {
          if (
            (parent.tagName && (
              parent.tagName.toLowerCase() === 'hdt-slider-reveal' ||
              parent.tagName.toLowerCase() === 'hdt-reval-items'
            )) ||
            parent.classList.contains('hdt-con-reveal--disabled')
          ) return true;

          if (parent.classList.contains('hdt-con-reveal--vertical')) {
            this.isRevealVertical = true;
          }
          parent = parent.parentElement;
      }
      return false;
    }

    getElemDirection(elem) {
        let dir = elem.getAttribute('reveal');
        if (!dir && elem.hasAttribute('can-reveal')) {
            dir = this.defaultDir;
        }
        dir = dir.trim().toLowerCase();
        if (dir && ['top', 'bottom', 'left', 'right'].includes(dir)) {
          if (this.isRevealVertical && ['left', 'right'].includes(dir)) {
            dir = 'top';
          }
          if (isRTL) {
            dir = dir == 'left' ? 'right' : dir == 'right' ? 'left' : dir;
          }
          return dir;
        }
        return this.options.direction;
    }

    getTransformVals(direction, distance, forInitial = true) {
        const d = parseFloat(distance);
        if (this.isRevealVertical && ['left', 'right'].includes(direction)) {
          direction = 'top';
        }
        switch (direction) {
          case 'top': return { x: 0, y: forInitial ? -d : 0 };
          case 'bottom': return { x: 0, y: forInitial ? d : 0 };
          case 'left': return { x: forInitial ? -d : 0, y: 0 };
          case 'right': return { x: forInitial ? d : 0, y: 0 };
          default: return { x: forInitial ? -d : 0, y: 0 }; // left
        }
    }

    setElemInitialState(elem, i) {
        const direction = this.getElemDirection(elem);
        const { x, y } = this.getTransformVals(direction, this.distance, true);
        elem.style.opacity = '0';
        elem.style.clipPath = '';
        elem.style.transform = `translate(${x}px, ${y}px)`;
    }

    revealElem(elem, i) {
        const direction = this.getElemDirection(elem);
        const { x, y } = this.getTransformVals(direction, this.distance, true);

        let delay = i * this.options.delay;
        if (i > 6) {
          if (i < 10) {
              delay = 6 * this.options.delay + i * 0.2 * this.options.delay;
          } else {
              delay = 8 * this.options.delay;
          }
        }

        let opacityTo = elem.hasAttribute('aria-disabled') ? 0.5 : 1;

        animate(
          elem,
          { opacity: [0, opacityTo], x: [x, 0], y: [y, 0] },
          { duration: this.options.duration, delay: delay, ease: this.options.ease, fill: 'forwards' }
        );
    }

    init() {
        this._stopFns = [];
        this.elems.forEach((elem, i) => {
            this.setElemInitialState(elem, i);
            const stop = inView(
              elem,
              (element, enterInfo) => {
                this.revealElem(element, i);
                if (!this.options.once) {
                  return (leaveInfo) => {
                    this.setElemInitialState(element, i);
                  };
                }
              },
              { amount: 0.1 }
            );

            this._stopFns.push(stop);
        });
    }

    destroy() {
        if (this._stopFns) {
            this._stopFns.forEach(stop => {
              try { stop && stop(); } catch (e) {}
            });
            this._stopFns = [];
        }
    }
}

function hdtInitRevealOnScroll(elem) {
    if (elem._hdtRevealInstance) elem._hdtRevealInstance.destroy();
    elem._hdtRevealInstance = new HdtRevealElementsOnScroll(elem);
}

function hdtInitAllRevealOnScroll(context = document) {
  if (matchMediaQuery("motion")) $$4('[reveal-on-scroll]', context).forEach(hdtInitRevealOnScroll);
}

hdtInitAllRevealOnScroll();

if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener(admEvts.seLoad, function(e) {
        hdtInitAllRevealOnScroll(e.target);
    });

    document.addEventListener(admEvts.seUnload, function(e) {
        $$4('[reveal-on-scroll]', e.target).forEach(elem => {
            if (elem._hdtRevealInstance) {
                elem._hdtRevealInstance.destroy();
                elem._hdtRevealInstance = null;
            }
        });
    });
}

class HdtAlwaysShowHc extends HTMLElement {
  constructor() {
    super();
    this.hotspots = [];
    this._onClick = this._onClick.bind(this);
    this._onOutsideClick = this._onOutsideClick.bind(this);
    this._observer = null;
    this._active = true;
    this._popoverChecked = false;
    this._onResize = this._onResize.bind(this);
    this._resizeTimer = null;
    this._ro = null;
    this.isLayered = false;
    this.container = null;
    this.hotspotsArea = null;
  }

  connectedCallback() {
    this.hotspots = [...$$4('.hdt-hotspot', this)];
    if (!this.hotspots.length) return;
    this.isLayered = this.classList.contains('hdt-always-show-hc--layered');
    this.container = $4('.hdt-image-hotspots-container', this);
    this.hotspotsArea = $4('.hdt-hotspots-area', this);
    this._updateHotspotsAreaDistance();
    this.hotspots.forEach(hotspot => {
      hotspot.addEventListener('click', this._onClick);
    });
    document.addEventListener('click', this._onOutsideClick);
    this._observer = new IntersectionObserver(this._onVisibilityChange.bind(this), {
      threshold: 0,
    });
    this._observer.observe(this);
    this._updateMetricsForAll();
    window.addEventListener('resize', this._onResize, { passive: true });
    this._ro = new ResizeObserver(() => {
      this._updateHotspotsAreaDistance();
      this._updateMetricsForAll();
    });
    this._ro.observe(this);
    if (this.container) this._ro.observe(this.container);
    if (this.hotspotsArea) this._ro.observe(this.hotspotsArea);

    if (window.Shopify && Shopify.designMode) {
      this._setupShopifyBlockSelection();
    }
  }

  disconnectedCallback() {
    this.hotspots.forEach(hotspot => {
      hotspot.removeEventListener('click', this._onClick);
    });
    document.removeEventListener('click', this._onOutsideClick);
    if (this._observer) this._observer.disconnect();
    if (this._ro) this._ro.disconnect();
    window.removeEventListener('resize', this._onResize);
  }

  _updateHotspotsAreaDistance() {
    if (!this.container || !this.hotspotsArea) return;
    const restoreContainer = this._ensureMeasurable(this.container);
    const restoreArea = this._ensureMeasurable(this.hotspotsArea);
    const cW = this.container.getBoundingClientRect().width;
    const aW = this.hotspotsArea.getBoundingClientRect().width;
    const distance = Math.max(0, (cW - aW) / 2);
    this.style.setProperty('--hsa-distance', `${distance.toFixed(2)}px`);
    if (restoreArea) restoreArea();
    if (restoreContainer) restoreContainer();
  }

  _onClick(e) {
    if (!this._active) return;
    const clicked = e.currentTarget;
    this.hotspots.forEach(h => h.classList.remove('hdt-hotspot--active'));
    clicked.classList.add('hdt-hotspot--active');
  }

  _onOutsideClick(e) {
    if (!this._active) return;
    const isInside = this.hotspots.some(hotspot => hotspot.contains(e.target));
    if (!isInside) {
      this.hotspots.forEach(h => h.classList.remove('hdt-hotspot--active'));
    }
  }

  _onResize() {
    clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      this._updateHotspotsAreaDistance();
      this._updateMetricsForAll();
    }, 60);
  }

  _onVisibilityChange(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        if (!this._popoverChecked) {
          this._checkAllPopoverOverflow();
          this._popoverChecked = true;
        }
        this._updateMetricsForAll();
        this._reactivate();
      } else {
        this._deactivate();
      }
    });
  }

  _reactivate() {
    if (this._active) return;
    this.hotspots.forEach(hotspot => {
      hotspot.addEventListener('click', this._onClick);
    });
    this._active = true;
  }

  _deactivate() {
    if (!this._active) return;
    this.hotspots.forEach(hotspot => {
      hotspot.removeEventListener('click', this._onClick);
      hotspot.classList.remove('hdt-hotspot--active');
    });
    this._active = false;
  }

  _updateMetricsForAll() {
    if (!this.hotspots?.length) return;
    this.hotspots.forEach(h => this._updateMetrics(h));
  }

  _updateMetrics(hotspot) {
    const btn = $4('.hdt-hotspot__btn', hotspot)
    const popover = $4('.hdt-hotspot__popover', hotspot);
    if (!btn || !popover) return;
    const restore = this._ensureMeasurable(popover);
    const btnRect = btn.getBoundingClientRect();
    const popRect = popover.getBoundingClientRect();
    const btnCx = btnRect.left + btnRect.width / 2;
    const btnCy = btnRect.top + btnRect.height / 2;
    const popCx = popRect.left + popRect.width / 2;
    const popCy = popRect.top + popRect.height / 2;
    const dxRaw = popCx - btnCx;
    const dy = popCy - btnCy;
    const dxRawAbs = Math.abs(dxRaw);
    const sign = dxRaw < 0 ? -1 : 1;
    const minAbs = btnRect.width;
    const dxAbs = Math.max(dxRawAbs, minAbs);
    const dx = sign * dxAbs;
    const dyAbs = Math.abs(dy);

    hotspot.style.setProperty('--hs-dx', `${dx.toFixed(2)}px`);
    hotspot.style.setProperty('--hs-dy', `${dy.toFixed(2)}px`);
    hotspot.style.setProperty('--hs-dx-abs', `${dxAbs.toFixed(2)}px`);
    hotspot.style.setProperty('--hs-dy-abs', `${dyAbs.toFixed(2)}px`);
    hotspot.style.setProperty('--hs-popover-w', `${popRect.width.toFixed(2)}px`);
    hotspot.style.setProperty('--hs-popover-h', `${popRect.height.toFixed(2)}px`);

    if (dxAbs < btnRect.width + btnRect.width) {
      hotspot.classList.add('hdt-small-line-1');
    } else {
      hotspot.classList.remove('hdt-small-line-1');
    }

    if (sign < 0) {
      hotspot.classList.add('hdt-negative-dir-line-1');
      hotspot.classList.remove('hdt-positive-dir-line-1');
    } else {
      hotspot.classList.add('hdt-positive-dir-line-1');
      hotspot.classList.remove('hdt-negative-dir-line-1');
    }

    if (restore) restore();
  }

  _ensureMeasurable(el) {
    const cs = window.getComputedStyle(el);
    if (cs.display !== 'none' && cs.visibility !== 'hidden') return null;
    const prev = {
      display: el.style.display,
      visibility: el.style.visibility,
      transform: el.style.transform,
      position: el.style.position,
    };
    el.style.display = 'block';
    el.style.visibility = 'hidden';
    el.style.transform = 'translateZ(0)';
    if (cs.position === 'static') el.style.position = 'relative';
    return () => {
      el.style.display = prev.display;
      el.style.visibility = prev.visibility;
      el.style.transform = prev.transform;
      el.style.position = prev.position;
    };
  }

  _checkAllPopoverOverflow() {
    if (!this.hotspotsArea) return;
    const hotspotsAreaRect = this.hotspotsArea.getBoundingClientRect();
    this.hotspots.forEach(hotspot => {
      const popover = $4('.hdt-hotspot__popover', hotspot);
      if (!popover) return;
      const popoverRect = popover.getBoundingClientRect();
      if (popoverRect.top < hotspotsAreaRect.top) {
        popover.classList.add('hdt-is-over-top');
      }
      if (popoverRect.bottom > hotspotsAreaRect.bottom) {
        popover.classList.add('hdt-is-over-bottom');
      }
    });
  }

  _setupShopifyBlockSelection() {
    document.addEventListener(admEvts.select, event => {
      const block = event.target;
      const hotspot = block.closest('.hdt-hotspot');
      if (hotspot) {
        hotspot.classList.add('hdt-hotspot--active');
      }
    });
    document.addEventListener(admEvts.deselect, event => {
      const block = event.target;
      const hotspot = block.closest('.hdt-hotspot');
      if (hotspot) {
        hotspot.classList.remove('hdt-hotspot--active');
      }
    });
  }
}
customElements.define('hdt-always-show-hc', HdtAlwaysShowHc);

class PopoverAnimate extends Popover {
  animateDialogOpen() {
    if (this._openedAsModal) {
      return super.animateDialogOpen();
    } else {
      this.updatePos(this.dialog.btnOpening, this.dialog, this.arrowEl, this.placement);
      // const sequence = [
      //   [ this.dialog, { top: ["10px", "0"], opacity: [0, 1], visibility: ["hidden", "visible"], '--inset-popover': ["50%", "0%"], '--popover-radius': ["40px", "0px"] }, { duration: 0.4 } ],
      //   [ $4('.hdt-popover__arrow', this), { opacity: [0, 1], transform: [`rotate(45deg) translateY(15px)`, `rotate(45deg) translateY(0)`] }, { at: ">", duration: 0.1 } ],
      // ];
      return animate(this.dialog, { top: ["10px", "0"], opacity: [0, 1], visibility: ["hidden", "visible"], '--inset-popover': ["50%", "0%"], '--popover-radius': ["40px", "0px"] }, { duration: 0.4 });
    }
  }

  animateDialogClose() {
    if (this._openedAsModal) {
      return super.animateDialogClose();
    } else {
      return animate(this.dialog, { top: ["0px", "10px"], opacity: [1, 0], visibility: ["visible", "hidden"], '--inset-popover': ["0%", "50%"], '--popover-radius': ["0px", "40px"] }, { duration: 0.25 });
    }
  }
}
customElements.define("hdt-popover-animate", PopoverAnimate);

class ModalStory extends Modal {
  #controller;
  constructor() {
    super();
    this._animating = false;
    this._phase = null;
    this._ghost = null;
    this._openTriggerEl = null;
    this._openPsiEl = null;
    this._openPsiRectSnapshot = null;
    this._openToken = null;
    this._openSeq = 0;
    this._productsStorySlider = null;
    this._radiusCache = new Map();
    this._marqueeCompensationPx = 25.5;
    this._marqueeAutoConfigured = null;
    this._marqueeHold = false;
    this._marqueeGuardAbort = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    let controller;
    const nav_slidestory = $4('.hdt-story-nav', this),
    _signal = this.#controller.signal,
    btnSound = $4('hdt-sound-video[current]', this),
    sliderButtons = $$4('.hdt-indicators--products-story button', this);

    this.dialog.addEventListener(dialogOpening, ()=> {
      $$4('hdt-video-hover[playing]', this.previousElementSibling).forEach( (el) => el.pause() );
      const story = $4('.hdt-slider--story', this),
      slide = this.dialog.btnOpening.parentElement,
      index = parseInt(slide?.dataset.index);
      sliderButtons.forEach((btn, i) => btn.setAttribute('aria-current', index == i));
      story.reInit();
      story.goToIndex(index, true);

      if (!slide.hasAttribute('has-video')) return;
      const video = $4('video', slide);
      btnSound?.toggleAttribute('hidden', !video);
      if (video) {
        btnSound?.update(video.muted);
        $$4('hdt-video video', this).forEach( (_video) => _video.muted = video.muted);
      }
    }, { signal: _signal });

    this.dialog.addEventListener(dialogOpen, ()=> {
      controller = new AbortController();
      const quickView = $id4('hdt-quick-view-modal'),
      story = $4('.hdt-slider--story', this),
      { signal } = controller;

      quickView?.addEventListener('dialog:ajax', () => {
        story?.pause();
      }, { signal });

      quickView?.addEventListener(dialogOpen, () => {
        story?.pause();
        $$4('hdt-video[playing]', story).forEach( (el) => el.pause() );
        nav_slidestory.style.setProperty('--hdt-modal-state','paused');
      }, { signal });

      quickView?.addEventListener(dialogClose, () => {
        story?.play();
        $4('.is-selected hdt-video', story)?.play();
        nav_slidestory.style.setProperty('--hdt-modal-state','running');
      }, { signal });

      if (story.apiS) {
        this.#videoModalFn(story.apiS.slideNodes()[story.apiS.selectedScrollSnap()]);
      } else {
        story.addEventListener('reInit', (evt)=> {
          const {sliderApi} = evt.detail;
          this.#videoModalFn(sliderApi.slideNodes()[sliderApi.selectedScrollSnap()]);
        }, { signal });
      }
      story.addEventListener('select', (evt)=> {
        const {sliderApi} = evt.detail;
        this.#videoModalFn(sliderApi.slideNodes()[sliderApi.selectedScrollSnap()], story, btnSound);
      }, { signal });

    }, { signal: _signal });

    this.dialog.addEventListener(dialogClosing, () => {
      const story = $4('.hdt-slider--story', this),
      hdtVideo = $4('.hdt-product-story-item.is-selected hdt-video', story);
      hdtVideo?.pause();
    }, { signal: _signal });

    this.dialog.addEventListener(dialogClose, () => {
      controller?.abort();
      btnSound?.setAttribute('hidden', '');

      if (this.hasAttribute('is--lazy')) return;
      // Play videos inView
      this.previousElementSibling.apiS.slidesInView().forEach( (index) => {
        const slide = this.previousElementSibling.apiS.slideNodes()[index];
        $4('hdt-video-hover', slide)?.play();
      });
    }, { signal: _signal });

    if (this.hasAttribute('is--lazy')) return;
    this.previousElementSibling?.addEventListener('slidesInView', (evt) => {
      const {sliderApi} = evt.detail;
      sliderApi.slidesNotInView().forEach( (index)=> this.#videoFn(sliderApi.slideNodes()[index]))
      sliderApi.slidesInView().forEach( (index)=> this.#videoFn(sliderApi.slideNodes()[index], 'play'))
    }, { signal: _signal });
  }

  #videoFn(slide, action = 'pause') {
    if (!slide?.hasAttribute('has-video')) return;
    const videoHover = $4('hdt-video-hover', slide);
    if (!videoHover) return;
    if (action == 'play') {
      //if ($4('video', videoHover).readyState >= 3) videoHover.play();
      this.#onVideoReady($4('video', videoHover), () => {
        if (this.previousElementSibling?.apiS?.slidesInView().includes(parseInt(slide.dataset.index))) videoHover.play();
      });
    } else {
      videoHover.pause();
    }
  }
  #onVideoReady(videoEl, callback) {
    if (videoEl.readyState >= 3) {
      callback();
    } else {
      videoEl.addEventListener('canplay', () => {
        callback();
      }, { once: true });
    }
  }
  #videoModalFn(slide, story, btnSound) {
    //console.log(slide);
    $$4('hdt-video[playing]', story).forEach( (el) => el.pause() );
    const hdtVideo = $4('hdt-video', slide);
    btnSound?.toggleAttribute('hidden', !hdtVideo);
    if (!hdtVideo) return;
    this.#onVideoReady($4('video', hdtVideo), () => {
      hdtVideo.play();
    });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  get #autoScroll() {
    return this._productsStorySlider.apiS.plugins()?.autoScroll;
  }

  wait(ms) { return new Promise(r => setTimeout(r, ms)); }
  _lock(phase) { if (this._animating) return false; this._animating = true; this._phase = phase; return true; }
  _unlock() { this._animating = false; this._phase = null; }
  _rect(el) { return el.getBoundingClientRect(); }

  _getStopMarqueeFlag() {
    const val = this.getAttribute('data-stop-marquee-on-open');
    if (val == null) return true;
    const v = String(val).toLowerCase();
    return !(v === 'false' || v === '0' || v === 'no' || v === 'off');
  }

  _getPsiInner(trigger) {
    if (!trigger) return null;
    try { return $4(':scope > .hdt-psi-inner', trigger) || $4('.hdt-psi-inner', trigger); }
    catch { return $4('.hdt-psi-inner', trigger); }
  }

  async _waitForJustClicked(maxMs = 5, stepMs = 16) {
    const sel = `.hdt-just-clicked[data-target="#${this.dialog?.id}"]`;
    const t0 = performance.now();
    let el = $4(sel, document);
    while (!el && (performance.now() - t0) < maxMs) {
      await this.wait(stepMs);
      el = $4(sel, document);
    }
    return el || null;
  }

  _calcTargetSizeFromPsiRect(psiRect) {
    const w0 = Math.max(1, Math.round(psiRect.width));
    const h0 = Math.max(1, Math.round(psiRect.height));
    const viewportW = Math.max(1, document.documentElement?.clientWidth || window.innerWidth || 1);
    const vwLimit = Math.max(1, viewportW - 30);
    const maxW = Math.min(580, vwLimit);
    const targetW = Math.min(Math.round(w0 * 2), Math.round(maxW));
    const k = targetW / w0;
    const targetH = Math.max(1, Math.round(h0 * k));
    return { targetW, targetH };
  }

  _applyDialogFixedSize(targetW, targetH) {
    const dlg = this.dialog;
    const prev = { w: dlg.style.width, h: dlg.style.height, opacity: dlg.style.opacity, visibility: dlg.style.visibility };
    dlg.style.width = `${targetW}px`;
    dlg.style.height = `${targetH}px`;
    return prev;
  }

  _resolveVarPx(varName, fallback = 12) {
    if (this._radiusCache.has(varName)) return this._radiusCache.get(varName);
    const tmp = document.createElement('div');
    tmp.style.position = 'absolute';
    tmp.style.visibility = 'hidden';
    tmp.style.borderTopLeftRadius = `var(${varName})`;
    document.body.appendChild(tmp);
    const px = parseFloat(getComputedStyle(tmp).borderTopLeftRadius) || fallback;
    tmp.remove();
    this._radiusCache.set(varName, px);
    return px;
  }

  _getOrCreateGhost() {
    if (!this._ghost) {
      const ghost = document.createElement('div');
      ghost.className = 'hdt-story-ghost';
      Object.assign(ghost.style, {
        position: 'fixed',
        transformOrigin: 'top left',
        pointerEvents: 'none',
        zIndex: '11',
        overflow: 'hidden'
      });
      document.body.appendChild(ghost);
      this._ghost = ghost;
    }
    return this._ghost;
  }

  _resetGhost(ghost, startRect, radiusPxStart, psi) {
    Object.assign(ghost.style, {
      left: `${startRect.left}px`,
      top: `${startRect.top}px`,
      width: `${startRect.width}px`,
      height: `${startRect.height}px`,
      clipPath: `inset(0 0 0 0 round ${radiusPxStart}px)`,
      transform: 'translate(0,0) scale(1,1)'
    });

    ghost.replaceChildren();
    const img = psi?.querySelector?.('img');
    if (img) {
      const gImg = img.cloneNode(false);
      gImg.src = img.currentSrc || img.src;
      gImg.alt = img.alt || '';
      const cs = getComputedStyle(img);
      Object.assign(gImg.style, {
        width: '100%',
        height: '100%',
        display: 'block',
        objectFit: cs.objectFit || 'cover',
        objectPosition: cs.objectPosition || 'center'
      });
      ghost.appendChild(gImg);
    } else {
      const div = document.createElement('div');
      div.style.width = '100%';
      div.style.height = '100%';
      div.style.background = psi ? getComputedStyle(psi).backgroundColor || '#000' : '#000';
      ghost.appendChild(div);
    }
  }

  _destroyGhost() {
    if (this._ghost) {
      const img = $4('img', this._ghost);
      if (img) { try { img.src = ''; } catch(e) {} }
      this._ghost.replaceChildren();
      this._ghost.remove();
      this._ghost = null;
    }
  }

  _calcTransform(startRect, endRect) {
    const dx = endRect.left - startRect.left;
    const dy = endRect.top  - startRect.top;
    const sx = Math.max(0.01, endRect.width  / Math.max(1, startRect.width));
    const sy = Math.max(0.01, endRect.height / Math.max(1, startRect.height));
    return { dx, dy, sx, sy };
  }

  _animateGhost(ghost, startRect, endRect, { duration = 0.48, easing = [0.19, 1, 0.22, 1], radiusFromPx, radiusToPx } = {}) {
    const { dx, dy, sx, sy } = this._calcTransform(startRect, endRect);
    return animate(
      ghost,
      {
        transform: [ 'translate(0px, 0px) scale(1, 1)', `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})` ],
        clipPath: [ `inset(0 0 0 0 round ${radiusFromPx}px)`, `inset(0 0 0 0 round ${radiusToPx}px)` ]
      },
      { duration, easing }
    );
  }

  _ensureSliderFromTrigger(trigger) {
    if (this._productsStorySlider && this._productsStorySlider.isConnected) return this._productsStorySlider;
    this._productsStorySlider = trigger?.closest?.('.hdt-slider--products-story') || null;

    if (this._productsStorySlider && this._marqueeAutoConfigured === null) {
      this._snapshotMarqueeAutoConfigured();
    }
    return this._productsStorySlider;
  }

  _getSliderApi() {
    const el = this._productsStorySlider;
    if (!el) return null;
    return el.apiS || el.api || el.slider || null;
  }

  _getAutoScrollPlugin() {
    const api = this._getSliderApi();
    if (!api) return null;
    try {
      const plugs = typeof api.plugins === 'function' ? api.plugins() : api.plugins;
      return plugs?.autoScroll || null;
    } catch {
      return null;
    }
  }

  _snapshotMarqueeAutoConfigured() {
    let configured = !!this._getAutoScrollPlugin();
    if (!configured) {
      const cfg = this._getSliderConfig();
      if (cfg && Object.prototype.hasOwnProperty.call(cfg, 'marquee')) {
        configured = Number(cfg.marquee) !== 0;
      }
    }
    this._marqueeAutoConfigured = !!configured;
  }

  async _stopMarquee(delayMs = 1) {
    if (!this._productsStorySlider) return false;
    if (delayMs > 0) await this.wait(delayMs);
    try {
      this.#autoScroll.stop();
      return true;
    } catch {
      return false;
    }
  }

  _playMarquee() {
    if (!this._productsStorySlider) return false;
    try {
      this.#autoScroll.play();
      return true;
    } catch {
      return false;
    }
  }

  _getSliderConfig() {
    const el = this._productsStorySlider;
    if (!el) return null;
    let cfg = null;
    try { cfg = el.config && typeof el.config === 'object' ? el.config : null; } catch {}
    if (!cfg) {
      const attr = el.getAttribute && el.getAttribute('config');
      if (attr) { try { cfg = JSON.parse(attr); } catch {} }
    }
    return cfg && typeof cfg === 'object' ? cfg : null;
  }

  _shouldApplyMarqueeCompensation() {
    const vp = Math.max(1, document.documentElement?.clientWidth || window.innerWidth || 1);
    if (vp < 1025) return false;
    const cfg = this._getSliderConfig();
    if (!cfg || !Object.prototype.hasOwnProperty.call(cfg, 'marquee')) return false;
    const m = Number(cfg.marquee) || 0;
    if (m === 0) return false;
    const stopFlag = this._getStopMarqueeFlag();
    if (stopFlag) return false;
    return true;
  }

  _compensateRectForMarquee(rect) {
    if (!this._shouldApplyMarqueeCompensation()) return rect;
    const cfg = this._getSliderConfig();
    const m = Number(cfg.marquee) || 0;
    const dir = m > 0 ? -1 : 1;
    const comp = Number(this._marqueeCompensationPx || 0);
    if (!comp) return rect;
    const left = rect.left + dir * comp;
    return { left, top: rect.top, width: rect.width, height: rect.height };
  }

  _enableMarqueeHoldGuards() {
    if (!this._productsStorySlider || this._marqueeGuardAbort) return;
    this._marqueeGuardAbort = new AbortController();
    const { signal } = this._marqueeGuardAbort;
    const reStop = () => { if (this._marqueeHold) { this._stopMarquee(0); } };
    const slider = this._productsStorySlider;
    slider.addEventListener('pointerenter', reStop, { signal, passive: true, capture: true });
    slider.addEventListener('pointerleave', reStop, { signal, passive: true, capture: true });
    slider.addEventListener('pointerup',    reStop, { signal, passive: true, capture: true });
    window.addEventListener('mouseup',      reStop, { signal, passive: true, capture: true });
    window.addEventListener('touchend',     reStop, { signal, passive: true, capture: true });
    setTimeout(() => reStop(), 0);
    setTimeout(() => reStop(), 60);
  }

  _disableMarqueeHoldGuards() {
    if (this._marqueeGuardAbort) {
      try { this._marqueeGuardAbort.abort(); } catch {}
      this._marqueeGuardAbort = null;
    }
  }
  async animateDialogOpen() {
    if (!this._lock('opening')) return animate(document.body, {}, { duration: 0 });
    const dlg = this.dialog;
    if (!dlg) { this._unlock(); return animate(document.body, {}, { duration: 0 }); }
    const token = ++this._openSeq;
    this._openToken = token;
    const prevVis = dlg.style.visibility;
    const prevOpacity = dlg.style.opacity;
    dlg.style.visibility = 'hidden';
    dlg.style.opacity = '0';
    const trigger = await this._waitForJustClicked(5, 16);
    if (!trigger) {
      dlg.style.visibility = prevVis || '';
      const ctrl = animate(
        dlg,
        { opacity: [0, 1], transform: ['translateY(12px)', 'translateY(0)'] },
        { duration: 0.3, ease: [0.19, 1, 0.22, 1] }
      );
      ctrl.finished.finally(() => { dlg.style.opacity = prevOpacity || ''; this._unlock(); });
      return ctrl;
    }

    const psi = this._getPsiInner(trigger);
    if (!psi) {
      dlg.style.visibility = prevVis || '';
      const ctrl = animate(
        dlg,
        { opacity: [0, 1], transform: ['translateY(12px)', 'translateY(0)'] },
        { duration: 0.3, ease: [0.19, 1, 0.22, 1] }
      );
      ctrl.finished.finally(() => { dlg.style.opacity = prevOpacity || ''; this._unlock(); });
      return ctrl;
    }
    this._openTriggerEl = trigger;
    this._openPsiEl = psi;
    this._ensureSliderFromTrigger(trigger);
    const stopFlag = this._getStopMarqueeFlag();
    if (stopFlag && this._marqueeAutoConfigured) {
      this._marqueeHold = true;
      await this._stopMarquee(0);
      this._enableMarqueeHoldGuards();
    }
    const startRect = this._rect(psi);
    this._openPsiRectSnapshot = { left: startRect.left, top: startRect.top, width: startRect.width, height: startRect.height };
    const { targetW, targetH } = this._calcTargetSizeFromPsiRect(startRect);
    const prevDlg = this._applyDialogFixedSize(targetW, targetH);
    dlg.getBoundingClientRect();
    const endRect = this._rect(dlg);
    const rStart = this._resolveVarPx('--rounded-product-card', 12);
    const rEnd   = this._resolveVarPx('--rounded', 16);
    const ghost = this._getOrCreateGhost();
    this._resetGhost(ghost, startRect, rStart, psi);
    dlg.style.visibility = prevVis || 'visible';
    const ghostControls = this._animateGhost(ghost, startRect, endRect, {
      duration: 0.5, ease: [0.19, 1, 0.22, 1], radiusFromPx: rStart, radiusToPx: rEnd
    });
    const fadeInDlg = animate(
      dlg, { opacity: [0, 1] },
      { duration: 0.16, delay: 0.34, ease: [0.19, 1, 0.22, 1] }
    );
    Promise.all([ghostControls.finished, fadeInDlg.finished])
      .catch(() => {})
      .finally(() => {
        if (this._marqueeHold) { this._stopMarquee(0); }
        this._destroyGhost();
        dlg.style.opacity = prevOpacity || '';
        this._unlock();
      });
    return ghostControls;
  }

  async animateDialogClose() {
    if (!this._lock('closing')) return animate(document.body, {}, { duration: 0 });
    const dlg = this.dialog;
    if (!dlg) { this._unlock(); return animate(document.body, {}, { duration: 0 }); }
    const tokenAtClose = this._openToken;
    const dlgRect = this._rect(dlg);
    let psi = (this._openPsiEl && this._openPsiEl.isConnected) ? this._openPsiEl : null;
    let endRect;
    if (psi) {
      endRect = this._rect(psi);
    } else if (this._openPsiRectSnapshot) {
      endRect = this._openPsiRectSnapshot;
    } else {
      const ctrl = animate(
        dlg,
        { opacity: [1, 0], transform: ['translateY(0)', 'translateY(12px)'] },
        { duration: 0.25, ease: [0.19, 1, 0.22, 1] }
      );
      ctrl.finished.finally(() => {
        this._marqueeHold = false;
        this._disableMarqueeHoldGuards();

        const stopFlag = this._getStopMarqueeFlag();
        const shouldResume = stopFlag && !!this._marqueeAutoConfigured;
        if (shouldResume && tokenAtClose === this._openToken) {
          this._playMarquee();
        }
        this._unlock();
      });
      return ctrl;
    }
    endRect = this._compensateRectForMarquee(endRect);
    const rStart = this._resolveVarPx('--rounded', 16);
    const rEnd   = this._resolveVarPx('--rounded-product-card', 12);
    const ghost = this._getOrCreateGhost();
    this._resetGhost(ghost, dlgRect, rStart, psi);
    const fadeOutDlg = animate(
      dlg, { opacity: [1, 0] },
      { duration: 0.16, ease: [0.19, 1, 0.22, 1] }
    );
    const ghostControls = this._animateGhost(ghost, dlgRect, endRect, {
      duration: 0.4, ease: [0.19, 1, 0.22, 1], radiusFromPx: rStart, radiusToPx: rEnd
    });
    Promise.all([ghostControls.finished, fadeOutDlg.finished])
      .catch(() => {})
      .finally(() => {
        this._destroyGhost();
        this._openTriggerEl = null;
        this._openPsiEl = null;
        this._openPsiRectSnapshot = null;
        this._marqueeHold = false;
        this._disableMarqueeHoldGuards();
        if (!(this._productsStorySlider?.isConnected)) this._productsStorySlider = null;
        const stopFlag = this._getStopMarqueeFlag();
        const shouldResume = stopFlag && !!this._marqueeAutoConfigured;
        if (shouldResume && tokenAtClose === this._openToken) {
          this._playMarquee();
        }
        this._unlock();
      });

    return ghostControls;
  }
}

class ControlModalStory extends HTMLElement {
    constructor() {
        super();
        this._observer = null;
        this.modal = null;
    }

    connectedCallback() {
        this.modal = $4(this.dataset.target);
        this._initObserver();
    }

    disconnectedCallback() {
        this._disconnectObserver();
        this._removeClickHandler();
        this.classList.remove('hdt-just-clicked');
    }

    _initObserver() {
        this._observer = new IntersectionObserver((entries) => {
            const entry = entries[0];
            if (entry.isIntersecting) {
                this._addClickHandler();
            } else {
                this._removeClickHandler();
                this._resetTargetWidth();
            }
        });
        this._observer.observe(this);
    }

    _disconnectObserver() {
      this._observer?.disconnect();
      this._observer = null;
    }

    _addClickHandler() {
        this.addEventListener('click', this.#onClick);
    }

    _removeClickHandler() {
        this.removeEventListener('click', this.#onClick);
    }

    #onClick = (e) => {
        const _this = this;
        if (!_this.contains(e.target)) return;
        const targetEl = $4(_this.dataset.target || '');
        if (!targetEl) return;
        const width = _this.offsetWidth;
        targetEl.style.setProperty('--item-width', `${width}px`);
        const sliderSelector = _this.dataset.slider;
        if (sliderSelector) {
            const slider = $4(sliderSelector);
            const parentSloder = _this.closest('.hdt-slider--products-story');
            const clickedSiblings = $$4('.hdt-product-story-item.hdt-just-clicked', parentSloder);
            clickedSiblings.forEach(el => {
                if (el !== _this) el.classList.remove('hdt-just-clicked');
            });
            _this.classList.add('hdt-just-clicked');
            // const sliderButtons = $$4('.hdt-indicators--products-story button', targetEl);
            // const slideIndex = _this.dataset.index;
            // setTimeout(() => {
            //     sliderButtons.forEach(btn => {
            //         btn.setAttribute('aria-current', false);
            //     });
            //     slider.goToIndex(slideIndex, true);
            //     setTimeout(() => slider.goToIndex(slideIndex, true), 60);
            //     const currentBtn = $4(`.hdt-indicators--products-story button[value="${slideIndex}"]`, targetEl);
            //     if (currentBtn) currentBtn.setAttribute('aria-current', true);
            // }, 10);
        }
    }

    _resetTargetWidth() {
        const targetEl = $4(this.dataset.target || '');
        targetEl?.style.removeProperty('--item-width');
    }
}

customElements.define('hdt-modal-story', ModalStory);
customElements.define('hdt-control-modal-story', ControlModalStory);

class HdtShrinkOnSroll extends HTMLElement {
  constructor() {
    super();
    this.inner = null;
    this.cleanupFns = [];
    this._onResize = this._onResize.bind(this);
  }

  connectedCallback() {
    if (!matchMediaQuery("motion")) return;
    this.inner = $4('.hdt-shrink-on-sroll-inner', this);
    window.addEventListener('resize', this._onResize);
    this._setupScrollEffect();
  }

  disconnectedCallback() {
    if (!matchMediaQuery("motion")) return;
    this._cleanup();
    window.removeEventListener('resize', this._onResize);
  }

  _cleanup() {
    this.cleanupFns.forEach(fn => fn && fn());
    this.cleanupFns = [];
  }

  _isMobile() {
    return window.innerWidth < 768;
  }

  _setupScrollEffect() {
    this._cleanup();
    const isExpand = this.dataset.expand === 'true';
    const expandFromBottom = this.dataset.expandFromBottom === 'true';
    const removeRoundOnExpand = this.dataset.expandRemoveRound !== 'false';
    if (this.getAttribute('mobile') === 'false' && this._isMobile()) {
      if (this.inner) this.inner.style.clipPath = '';
      return;
    }
    if (isExpand && expandFromBottom) {
      this._setupBottomExpandEffect(removeRoundOnExpand);
      return;
    }
    if (this.inner) {
      const fromClip = isExpand ? `inset(0 var(--clip-x) 0 var(--clip-x) round var(--rounded-lg))` : `inset(0 round 0)`;
      const toClip = isExpand ? (removeRoundOnExpand ? `inset(0 round 0)` : `inset(0 round var(--rounded-lg))`) : `inset(0 var(--clip-x) 0 var(--clip-x) round var(--rounded-lg))`;
      const offsets = isExpand ? ['0 1', '1 0.7'] : ['1 0.8', '1 0.1'];
      this.cleanupFns.push(
        scroll( animate(this.inner, { clipPath: [fromClip, toClip] }), { offset: offsets, target: this } )
      );
    }
    this._setupTranslateEffects(isExpand);
  }

  _setupBottomExpandEffect(removeRoundOnExpand = true) {
    const el = this;
    const inner = this.inner;
    if (!inner) return;
    const START_GAP = 60;
    const END_GAP = 80;
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const bottom = rect.bottom;
      let progress;
      const start = vh - START_GAP;
      const end = END_GAP;
      if (bottom >= start) {
        progress = 0;
      } else if (bottom <= end) {
        progress = 1;
      } else {
        progress = (start - bottom) / (start - end);
      }
      this._applyExpandProgress(progress, removeRoundOnExpand);
      this._applyTranslateProgress(progress);
    };

    // onScroll();
    // window.addEventListener('scroll', onScroll, { passive: true });
    // this.cleanupFns.push(() => window.removeEventListener('scroll', onScroll));
    this.cleanupFns.push(scroll(onScroll));
  }

  _applyExpandProgress(progress, removeRoundOnExpand = true) {
    const p = Math.min(Math.max(progress, 0), 1);
    if (removeRoundOnExpand) {
      this.inner.style.clipPath = `inset(0 calc(var(--clip-x) * ${1 - p}) 0 calc(var(--clip-x) * ${1 - p}) round calc(var(--rounded-lg) * ${1 - p}))`;
    } else {
      this.inner.style.clipPath = `inset(0 calc(var(--clip-x) * ${1 - p}) 0 calc(var(--clip-x) * ${1 - p}) round var(--rounded-lg))`;
    }
  }

  _applyTranslateProgress(progress) {
    const isMobile = this._isMobile();
    const candidates = $$4('[data-shrink-translate], [data-shrink-translate_mb]', this);
    const p = Math.min(Math.max(progress, 0), 1);
    candidates.forEach(el => {
      const attrName = isMobile ? 'data-shrink-translate_mb' : 'data-shrink-translate';
      if (!el.hasAttribute(attrName)) return;
      const dir = (el.getAttribute(attrName) || '').trim();
      if (!dir) return;
      const base = dir === 'start' ? 'var(--clip-x)' : dir === 'end' ? 'calc(var(--clip-x) * -1)' : '0px';
      el.style.transform = `translateX(calc(${base} * ${1 - p}))`;
    });
  }

  _setupTranslateEffects(isExpand = false) {
    const isMobile = this._isMobile();
    const candidates = $$4('[data-shrink-translate], [data-shrink-translate_mb]', this);
    candidates.forEach(el => {
      const attrName = isMobile ? 'data-shrink-translate_mb' : 'data-shrink-translate';
      if (!el.hasAttribute(attrName)) return;
      let dir = (el.getAttribute(attrName) || '').trim(); // "start" | "end"
      if (!dir) return;
      dir = isRTL ? dir == 'start' ? 'end': 'start': dir;
      let fromVal = dir === 'start' ? 'calc(var(--clip-x) * 1)' : dir === 'end' ? 'calc(var(--clip-x) * -1)' : '0px';
      let toVal = '0px';
      if (!isExpand) {
        fromVal = '0px';
        toVal = dir === 'start' ? 'calc(var(--clip-x) * 1)' : dir === 'end' ? 'calc(var(--clip-x) * -1)' : '0px';
      }
      const offsets = isExpand ? ['0 1', '1 0.7'] : ['1 0.8', '1 0.1'];
      const stop = scroll(
        animate(el, { transform: [`translateX(${fromVal})`, `translateX(${toVal})`] }),
        { offset: offsets, target: this }
      );
      this.cleanupFns.push(stop);
    });
  }

  _onResize() {
    this._setupScrollEffect();
  }
}

customElements.define('hdt-shrink-on-sroll', HdtShrinkOnSroll);

class btnAutoScroll extends HTMLElement {
  #btn;
  #wPlay;
  #wPause;
  #auto;
  connectedCallback() {
    this.#btn    = $4('.hdt-btn-control_media', this);
    this.#wPlay  = $4('.hdt-ic-play-wrap', this)
    this.#wPause = $4('.hdt-ic-pause-wrap', this);

    const slider = $id4(`${this.#btn.getAttribute('aria-controls')}`);
    this.toggleAttribute('hidden', !slider.apiS || slider.apiS.scrollSnapList().length < 2 || !this.#active(slider));
    if(!slider.apiS) return;
    this.#auto = slider.apiS.plugins()?.autoScroll;
    this.#btn.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.#isPlaying) {
        this.#auto.stop();
      } else {
        this.#auto.play();
      }
    });
    slider.apiS.on('autoScroll:play', ()=> this.#sync(true));
    slider.apiS.on('autoScroll:stop', ()=> this.#sync(false));
    slider.apiS.on('reInit', (apiS)=> {
      this.toggleAttribute('hidden', apiS.scrollSnapList().length < 2 || !this.#active(slider));
    });
    if (this.#isPlaying) this.#sync();
  }
  #active(slider) {
    return slider.autoScrollActive && slider.autoScrollActive();
  }

  get #isPlaying() { return this.#auto?.isPlaying(); }

  #sync(playing = null) {
    if (playing == null) playing = this.#isPlaying;
    //console.log('playing: ', playing)
    this.#wPlay.toggleAttribute('hidden',  playing);
    this.#wPause.toggleAttribute('hidden', !playing);
    this.#btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
  }
}
customElements.define('hdt-pause-play-slider', btnAutoScroll);

class HeroCollections extends HTMLElement {
  #collectionsScroll;
  #collectionsScrollInner;
  #resize;
  #controller;
  constructor() {
    super();
    this._cleanupFns = [];
    this._focusScrollToken = 0;
    this._focusRecheckTimer = null;
    this.#collectionsScroll = $4('.hdt-collections-tilted-scroll', this);
    this.#collectionsScrollInner = $4('.hdt-collections-tilted-scroll-inner', this);
  }

  connectedCallback() {
    this.#controller = new AbortController();
    this.#init();
    this.#resize = resize(() => {
      this.#resetNonDesktopStyles();
      this.#cancelFocusScroll();
      this._cleanup(false);
      frame.render(() => this.#init());
    });
  }

  disconnectedCallback() {
    this._cleanup();
    this.#resize();
  }

  _cleanup(reset = true) {
    this.#controller?.abort();
    this.#cancelFocusScroll();
    this._cleanupFns.forEach(fn => fn && fn());
    this._cleanupFns = [];
    if (!this.#isDesktop && reset) this.#resetNonDesktopStyles();
  }

  #cancelFocusScroll() {
    this._focusScrollToken++;
    if (this._focusRecheckTimer) {
      clearTimeout(this._focusRecheckTimer);
      this._focusRecheckTimer = null;
    }
  }

  _clamp(v, min = 0, max = 1) {
    return Math.min(max, Math.max(min, v));
  }

  get #isDesktop() {
    return matchMediaQuery("hover") && window.innerWidth > 1024;
  }

  #resetNonDesktopStyles() {

    const collectionsContent = $4('.hdt-hero-collections-content', this);
    const sectionHeaderWrap = $4('.hdt-section__header-wrap', this);
    const sectionHeader = $4('.hdt-section__header', this);
    const titleCards = $$4('.hdt-cl-tilted-card', this);

    this.style.removeProperty('--content-height');

    collectionsContent?.style.removeProperty('clip-path');
    collectionsContent?.style.removeProperty('transform');
    sectionHeaderWrap?.style.removeProperty('transform');
    this.#collectionsScrollInner?.style.removeProperty('transform');
    sectionHeader?.style.removeProperty('opacity');
    sectionHeader?.style.removeProperty('transform');
    this.#collectionsScroll?.style.removeProperty('--opacity-progress');

    if (titleCards && titleCards.length) {
      titleCards.forEach(card => {
        card.style.removeProperty('--angle-percent');
      });
    }
  }

  #getSectionScrollBounds() {
    const rect = this.getBoundingClientRect();
    const startY = window.scrollY + rect.top;
    const endY = window.scrollY + rect.bottom - window.innerHeight;
    return [startY, endY];
  }

  #getProgress() {
    const [startY, endY] = this.#getSectionScrollBounds(),
    denom = Math.max(1, endY - startY);
    return this._clamp((window.scrollY - startY) / denom, 0, 1);
  }

  #scrollCardIntoView(card, attempt = 0, token = this._focusScrollToken) {
    if (token !== this._focusScrollToken) return;
    if (!this.#collectionsScroll || !this.#collectionsScrollInner || !card) return;
    const active = document.activeElement;
    if (!active || !card.contains(active)) return;
    const vwRect = this.#collectionsScroll.getBoundingClientRect();
    const cr = card.getBoundingClientRect();
    let overscan = 24;
    if (this.dataset && this.dataset.focusOverscan) {
      const v = parseFloat(this.dataset.focusOverscan);
      if (!Number.isNaN(v)) overscan = v;
    } else {
      const cssVar = getComputedStyle(this.#collectionsScroll).getPropertyValue('--focus-overscan').trim();
      const v = parseFloat(cssVar);
      if (!Number.isNaN(v)) overscan = v;
    }
    const leftInView  = cr.left  - vwRect.left;
    const rightInView = cr.right - vwRect.left;
    const leftThreshold  = isRTL ? overscan : 0;
    const rightThreshold = isRTL ? vwRect.width : (vwRect.width - overscan);
    const fullyVisible = leftInView >= leftThreshold && rightInView <= rightThreshold;
    if (fullyVisible) return;
    let deltaPx = 0;
    if (leftInView < leftThreshold) {
      deltaPx = (leftThreshold - leftInView) + 1;
    } else if (rightInView > rightThreshold) {
      deltaPx = (rightThreshold - rightInView) - 1;
    } else {
      return;
    }

    const containerW = vwRect.width;
    const innerW = this.#collectionsScrollInner.getBoundingClientRect().width;
    const span = Math.max(0, innerW - containerW);
    if (span === 0) return;
    const dir = isRTL ? +1 : -1;
    const dP = (deltaPx * dir) / span;
    const pNow = this.#getProgress();
    const pTarget = this._clamp(pNow + dP, 0, 1);
    const [startY, endY] = this.#getSectionScrollBounds();
    const totalLen = Math.max(1, endY - startY);
    const targetY = Math.round(startY + pTarget * totalLen);
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (token !== this._focusScrollToken) return;
    if (!document.activeElement || !card.contains(document.activeElement)) return;
    window.scrollTo({ top: targetY, behavior: prefersReduced ? 'auto' : 'smooth' });
    if (attempt < 2) {
      this._focusRecheckTimer = setTimeout(() => {
        if (token !== this._focusScrollToken) return;
        const a = document.activeElement;
        if (!a || !card.contains(a)) return;
        this.#scrollCardIntoView(card, attempt + 1, token);
      }, prefersReduced ? 0 : 360);
    }
  }

  #init() {
    const collectionsWidth = this.#collectionsScroll.clientWidth,
    collectionsInnerWidth = this.#collectionsScrollInner.clientWidth;
    if (!this.#isDesktop || !(collectionsInnerWidth > collectionsWidth)) return;
    const titleCards = $$4('.hdt-cl-tilted-card', this),
    sectionHeaderWrap = $4('.hdt-section__header-wrap', this),
    sectionHeader = $4('.hdt-section__header', this),
    n = titleCards.length;

    if (this.#collectionsScrollInner) {
      const bgAnimate = $4('.hdt-hero-bg-animate', this);
      if (bgAnimate) {
        const collectionsContent = $4('.hdt-hero-collections-content', this),
        height = collectionsContent.getBoundingClientRect().height;
        this.style.setProperty('--content-height', `${height}px`);
        const inset = 'inset(0 0 0 0 round 0px)',
        inset2 = 'inset(20px round var(--rounded, 0px))',
        inset3 = 'inset(0px 20px round var(--rounded, 0px))',
        sequence = [
          [bgAnimate, { clipPath: [ inset, inset2, 'inset(calc(50vh - var(--content-height) * 0.5) 30px calc(50vh - var(--content-height) * 0.5) var(--clip-x, 3rem) round var(--rounded-lg, 0px))', inset2 ] }],
          [collectionsContent, { clipPath: [ inset, inset3, 'inset(0px 30px round var(--rounded-lg, 0px))', inset3 ] }, { at: "<" }]
        ];
        if (sectionHeaderWrap) sequence.push([sectionHeaderWrap, { x: ['0px', 'calc(var(--value-logical-flip) * 30px)']}, { at: "<" }]);
        this._cleanupFns.push(
          scroll(
            animate(sequence),
            { offset: ["0% end", "22% end", "78% end", "95% end"], target: this }
          )
        );
        // this._cleanupFns.push(
        //   scroll((progress) => {
        //       this.classList.toggle('hdt-show-bg-fade-ani', progress >= 0.99);
        //     },
        //     { offset: ["start end", "end end"], target: this }
        //   )
        // );
      }

      let translateX = `${collectionsWidth - collectionsInnerWidth}px - var(--scrollbar-w, 0px) - 30px - var(--padding-inline)`;
      if (isRTL) translateX = `-1 * (${translateX})`;
      translateX = `calc(${translateX})`;
      this._cleanupFns.push(
        scroll(
          animate(this.#collectionsScrollInner, {
            x: ['0px', translateX],
          }),
          { offset: ["start start", "end end"], target: this }
        )
      );
      if (titleCards) {
        this.#collectionsScroll.classList.add('hdt-has-cards');
        this._cleanupFns.push(
          scroll(
            (progress) => {
              this.#collectionsScroll.style.setProperty('--opacity-progress', progress >= 0.9 ? (progress - 0.9) / 0.1 : 0);
            },
            { offset: ["start start", "end end"], target: this }
          )
        );
        this._cleanupFns.push(
          scroll(
            (progress) => {
              const threshold = 0.5;
              titleCards.forEach((card, i) => {
                const start = i / n;
                const end = (i + threshold) / n;
                let anglePercent = 1;
                if (progress <= start) {
                  anglePercent = 1;
                }
                else if (progress >= end) {
                  anglePercent = 0;
                } else {
                  anglePercent = 1 - (progress - start) / (end - start);
                }
                card.style.setProperty('--angle-percent', anglePercent);
              });
            },
            { target: this }
          )
        );

        titleCards.forEach(card => {
          const cardInner = $4('.hdt-collection-card-inner', card);
          if (!cardInner) return;
          if (!cardInner.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(cardInner.tagName)) {
            cardInner.setAttribute('tabindex', '0');
          }
        });
        const { signal } = this.#controller;
        this.#collectionsScroll.addEventListener('focusin', (e) => {
          const inner = e.target.closest('.hdt-collection-card-inner');
          if (!inner || !this.#collectionsScroll.contains(inner)) return;
          this.#cancelFocusScroll();
          const card = inner.closest('.hdt-cl-tilted-card');
          const token = this._focusScrollToken;
          this.#scrollCardIntoView(card, 0, token);

        }, { capture: true, signal });
        document.addEventListener('focusin', (e) => {
          if (!this.contains(e.target)) this.#cancelFocusScroll();
        }, { capture: true, signal });
      }
    } // end if (this.#collectionsScrollInner)

    if (sectionHeader) {
      this._cleanupFns.push(
        scroll(
          animate(sectionHeader, { opacity: [1, 0], scale: [1, 0.82] }),
          { offset: ["start start", "200px"], target: this }
        )
      );
    }
  }
}
customElements.define('hdt-hero-collections', HeroCollections);

// XOAN =============================================

//Counter number
class Counter extends HTMLElement {
  #animation;
  #inView;
  constructor() {
    super();
  }
  connectedCallback() {
    if (!matchMediaQuery("motion")) {
      animate([
        [this.lastElementChild, { opacity: [1, 0] }, { duration: 0 }],
        [this.firstElementChild, { opacity: [0, 1] }, { duration: 0 }],
      ]);
      return;
    }
    const target = parseInt(this.dataset.counter) || 0;
    const duration = matchMediaQuery("motion") ? parseFloat(this.dataset.duration) || 5 : 0;
    this.#animation = animate(0, target, {
      duration,
      ease: "circOut",
      onUpdate: (latest) => {
        this.lastElementChild.textContent = Math.round(latest);
      },
    });
    this.#animation.pause();
    this.#inView = inView(this, () => {
      this.#animation.play()
      return () => this.#animation.complete()
    }, { amount: 0.5 });
  }
  disconnectedCallback() {
    if (!matchMediaQuery("motion")) return;
    this.#animation.cancel();
    this.#inView();
  }
}
customElements.define("hdt-counter", Counter);
//Scroll section
class ShirkScroll extends HTMLElement {
    constructor() {
      super();
      this.mediaQueryList = window.matchMedia('(min-width: 768px)');
      this.offsetStart = 0;
      this.scrollInstances = []
      this.minScale = 0;
    }
    connectedCallback() {
        const rect = this.getBoundingClientRect()
        const devHeight = window.innerHeight
        if(rect.height < devHeight){
            this.offsetStart = 0.3
        }
        this.boundAnimateSection = this.animateSection.bind(this);
        this.mediaQueryList.addEventListener('change', this.boundAnimateSection);
        this.boundAnimateSection();
    }
    animateSection(){
      const shouldAnimate = this.mediaQueryList.matches
      if(shouldAnimate == false){
          this.resetTransforms();
          return
      }
      if ( this.hasAttribute('on-shirk-scroll') ) {
        const contentLeft = $$4('[transLeft]', this);
        const contentRight = $$4('[transRight]', this);
        const mediaShirk = $$4('.hdt-media-wrapper:not([unless]) > :is(img, svg), .hdt-media-wrapper:not([unless]) hdt-video > :is(img, video, iframe)', this);
        const scaleMax = parseFloat(getComputedStyle(this).getPropertyValue('--scale-max'));
        let sequence = [];
        sequence =[
          [ this, { clipPath: [`inset(0 0 0 0 round 0)`,`inset(0 var(--clip-path-off) 0 var(--clip-path-off) round var(--rounded-lg, 0))`] }, { ease: 'linear'} ]
        ]
        if(mediaShirk.length){
          sequence.push([
            mediaShirk,
            { transform: [`scale(${scaleMax})`, `scale(1)`] },
            { ease: "linear", at: "<" }
          ])
        }
        if(contentLeft.length){
          sequence.push([
            contentLeft,
            { transform: [`translateX(0px)`, `translateX(calc(1 * var(--value-logical-flip) * var(--clip-path-off)))`] },
            { ease: "linear", at: "<" }
          ]);
        }
        if(contentRight.length){
          sequence.push([
            contentRight,
            { transform: [`translateX(0px)`, `translateX(calc(-1 * var(--value-logical-flip) * var(--clip-path-off)))`] },
            { ease: "linear", at: "<" }
          ]);
        }
        this.currentAnimate = animate(sequence)
        scroll(
          this.currentAnimate,
          {
            target: this,
            offset: ["start end", `end ${this.offsetStart}`]
          }
        )
      }
    }
    resetTransforms() {
      this.currentAnimate?.cancel()
      this.currentAnimate?.stop()
    }
}
customElements.define("hdt-shirk-scroll", ShirkScroll);

//Testimonial image
const dispScale = springValue(100);
svgEffect($id4('hdt-feDisplacementMap'), { attrScale: dispScale })
class TestimonialImage extends ShirkScroll{
    #imagesTes;
    #imageW;
    #zIndexMap;
    connectedCallback(){
        if (this.hasAttribute('on-shirk-scroll')) super.connectedCallback();
        this.#imagesTes = $$4('.hdt-testimonial-image', this);
        this.#imageW = $4('.hdt-inner-list-images-testimonial .hdt-testimonial-image', this).offsetWidth + 40
        this.#zIndexMap = this.#imagesTes.map((img, i) => parseInt(img.style.zIndex || 0) || 0)

          $4('hdt-slider', this)?.addEventListener('select', (event) => {
            const {selectedScrollSnap, previousScrollSnap} = event.detail.sliderApi,
            targetSelect = selectedScrollSnap(),
            targetPrev = previousScrollSnap();            
            this.#animateAndReorder(targetSelect, targetPrev);
          })
    }

    #animateAndReorder(targetIndex, otherIndex = null) {
        const maxI = this.#imagesTes.length - 1
        let direction = targetIndex < otherIndex ? 'left' : 'right';
        const wrapLeft  = targetIndex < otherIndex && targetIndex === 0 && otherIndex === maxI;
        const wrapRight = targetIndex > otherIndex && targetIndex === maxI && otherIndex === 0;
        if (wrapLeft)  direction = 'right';
        if (wrapRight) direction = 'left';
        const offset = direction === 'left' ? -this.#imageW : this.#imageW;
        let animateTarget;
        if (wrapLeft) {
          animateTarget = this.#imagesTes[otherIndex];
        } else if (wrapRight) {
            animateTarget = this.#imagesTes[targetIndex];
        } else {
            animateTarget = this.#imagesTes[targetIndex < otherIndex ? targetIndex : otherIndex];
        }
        animate(
            animateTarget,
            { x: [0, offset] },
            { duration: 0.25 }
        ).then(() => {
          this.#imagesTes[targetIndex].style.zIndex = this.#imagesTes.length;
          this.#zIndexMap[targetIndex] = this.#imagesTes.length;

          let delta;
          if (wrapLeft) {
              delta = 1;
              this.#imagesTes[otherIndex].style.zIndex = 1;
              this.#zIndexMap[otherIndex] = 1;

          } else if (wrapRight) {
              delta = -1;
              this.#imagesTes[otherIndex].style.zIndex = maxI;
              this.#zIndexMap[otherIndex] = maxI;

          } else {
              const forward = targetIndex < otherIndex;
              delta = forward ? -1 : 1;

              const z = forward ? maxI : 1;
              this.#imagesTes[otherIndex].style.zIndex = z;
              this.#zIndexMap[otherIndex] = z;
          }
          // Update z-index for other image
          this.#imagesTes.forEach((img, idx) => {
            if (idx !== targetIndex && idx !== otherIndex) {
              // const delta = direction === 'left' ? -1 : 1;
              const newZ = (this.#zIndexMap[idx] || 0) + delta;
              img.style.zIndex = newZ;
              this.#zIndexMap[idx] = newZ;
            }
          })
          // Animate to return to the initial/original position
          animate(
              animateTarget,
              { x: [offset, 0] },
              { duration: 0.25 }
          )
        })
    }
}
customElements.define("hdt-testimonial-image", TestimonialImage);


class productHoverTitle extends HTMLElement {
  #hover;
  #controller;
  connectedCallback(){
    if (matchMediaQuery("hover")) this.#hoverTitleproduct();
  }
  disconnectedCallback(){
    this.#hover?.()
    this.#controller?.abort();
  }
  #hoverTitleproduct(){
    const titleItem = $4('.hdt-product-title', this),
    //imgPr = $4('.hdt-product-image', this),
    imgWrapper = $4('.hdt-product-image-wrapper', this),
    left = springValue(0),
    top = springValue(this.#height(imgWrapper));
    styleEffect(imgWrapper, { left, top });

    // Mouse move title
    this.#controller = new AbortController();
    //let rafId = null;
    titleItem.addEventListener('mousemove', (e) => {
      if (!titleItem.isHover) return;
      frame.render( ()=> this.#updatePosition(e, titleItem, imgWrapper, left, top) );
      //rafId = requestAnimationFrame(()=> this.#updatePosition(e, titleItem, imgWrapper, left, top));
    }, { signal: this.#controller.signal });
    
    // Hover title
    this.#hover = hover(titleItem, () => {
      titleItem.isHover = true;
      dispScale.set(0);
      top.set(this.#height(imgWrapper));
      //animate(dispScale, 0, { duration: 0.7, ease: [0.23, 1, 0.32, 1] });
      animate(imgWrapper, { opacity: [0, 1] }, { duration: 0.15 });
      
      return () => {
        titleItem.isHover = false;
        cancelFrame(this.#updatePosition);
        //cancelAnimationFrame(rafId);
        
        //animate(dispScale, 180, { duration: 0.4, ease: 'ease-in' });
        dispScale.set(100);
        animate(imgWrapper, { opacity: [1, 0] }, { duration: 0.4, delay: 0.1 });
      };
    });
  }
  #updatePosition(e, titleItem, imgWrapper, left) {
    const mouseX = e.clientX,
    mouseY = e.clientY;

    const wrapRect = titleItem.getBoundingClientRect(),
    imgRect = imgWrapper.getBoundingClientRect();

    // Original position: title is centered vertically in the image, image is centered horizontally.
    const baseX = (mouseX - wrapRect.left) - imgRect.width / 2;
    //baseY =  - imgRect.height - 15;

    // Small offset by mouse
    const titleWidth = wrapRect.width,
    relX = ((mouseX - wrapRect.left) / titleWidth - 0.5) * 2;   // -1 to 1

    const offsetRange = 25, // px, adjust the rocking motion
    targetX = baseX + relX * offsetRange;
    //targetY = baseY;
    left.set(targetX);
    // top.set(targetY);
  }
  #height(el) {
    return -1 * ((el?.clientHeight || 0) + 15);
  }
}
customElements.define("hdt-pr-hover", productHoverTitle);

// Scroll stack
class HeroScrollStack extends HTMLElement {
  constructor() {
    super();
    this.scrollControllers = [];
    this._isFocusScrolling = false;
    this._focusScrollTimeout = null;
  }

  connectedCallback() {
    this.groupStackScroll();
    this._bindFocusScroll();
  }

  groupStackScroll() {
    const items = $$4('.hdt-group-stack-scroll .hdt-hero-media', this);
    const groupStack = $4('.hdt-group-stack-scroll', this);
    const offset = items.length - 1;

    if (!items.length) return;
    if (this._isFocusScrolling) return;
    scroll(
      animate(items[0], {
          transform: [
              "scale3d(0.5, 0.5, 1)",
              "scale3d(1, 1, 1)"
          ]
      }),
      {
          target: this,
          offset: ["start end", "start 30vh"]
      }
    )
    scroll(
      animate(
        groupStack,
        {
          transform: [
            'none',
            `translateX(calc((-${offset * 100}% - ${offset} * var(--gap-hero)) * var(--value-logical-flip)))`
          ]
        }
      ),
      {
        target: this
      }
    );
    scroll(progress => {
      const step =  1/(items.length - 1)
      let dir = isRTL ? -1 : 1;
      items.forEach((item, idx) => {
          const wrapper = $4('.hdt-media-wrapper img', item);
          if (!wrapper) return;
          let start;
          let end
          let baseTranslate
          if(idx === 0){
              start = 0
              end = step
              baseTranslate = 10
          }else{
              start = step * (idx - 1);
              if(idx === items.length - 1){
                  end = step * idx;
              }else{
                  end = step * (idx + 0.5);
              }
              baseTranslate = 30
          }
          const localProgress = Math.min(1, Math.max(0, (progress - start) / (end - start)));
          const translateX = (-baseTranslate + baseTranslate * localProgress) * dir;
          wrapper.style.transform = `translateX(${translateX}%)`;
        })
    },
    {
        target: this,
        offset: ["start start", "end end"]
    });
  }

  _bindFocusScroll() {
    const groupStack = $4('.hdt-group-stack-scroll', this);
    const items = $$4('.hdt-group-stack-scroll .hdt-hero-media', this);
    if (!groupStack || !items.length) return;
    const prefersReducedMotion = !matchMediaQuery("motion");
    const getScrollTargetForIndex = (index) => {
      const rect = this.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const sectionTopAbs = window.scrollY + rect.top;
      const sectionHeight = rect.height;
      const scrollStart = sectionTopAbs;
      const scrollEnd = sectionTopAbs + sectionHeight - viewportHeight;
      if (scrollEnd <= scrollStart) {
        return scrollStart;
      }
      const maxStep = Math.max(items.length - 1, 1);
      let logicalIndex = index;
      const progress = maxStep === 0 ? 0 : logicalIndex / maxStep;
      return scrollStart + progress * (scrollEnd - scrollStart);
    };

    const focusableSelector = `.hdt-hero-media button, .hdt-hero-media [role="button"], .hdt-hero-media a[href]:not([tabindex="-1"]), .hdt-hero-media [tabindex]:not([tabindex="-1"])`;
    const getFocusableElements = () => Array.from($$4(focusableSelector, this)) .filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1);
    const onFocusIn = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const isButtonLike =
        target.tagName === 'BUTTON' ||
        target.getAttribute('role') === 'button' ||
        (target.tagName === 'A' && target.href);

      if (!isButtonLike) return;

      const media = target.closest('.hdt-hero-media');
      if (!media || !this.contains(media)) return;

      const index = Array.prototype.indexOf.call(items, media);
      if (index < 0) return;

      const rawTargetTop = getScrollTargetForIndex(index);
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const maxScrollable = document.documentElement.scrollHeight - viewportHeight;
      const finalTop = Math.min( Math.max(0, rawTargetTop), Math.max(0, maxScrollable) );
      this._isFocusScrolling = true;
      clearTimeout(this._focusScrollTimeout);

      window.requestAnimationFrame(() => {
        window.scrollTo({ top: finalTop, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
      });

      this._focusScrollTimeout = setTimeout(() => {
        this._isFocusScrolling = false;
      }, prefersReducedMotion ? 0 : 600);
    };

    const onKeyDown = (event) => {
      if (event.key !== 'Tab' || event.defaultPrevented) return;
      const active = document.activeElement;
      if (!active || !this.contains(active)) return;
      const focusables = getFocusableElements();
      if (!focusables.length) return;
      const currentIndex = focusables.indexOf(active);
      const dir = event.shiftKey ? -1 : 1;
      let nextIndex;
      if (currentIndex === -1) {
        nextIndex = dir > 0 ? 0 : focusables.length - 1;
      } else {
        nextIndex = currentIndex + dir;
      }

      if (nextIndex < 0 || nextIndex >= focusables.length) {
        return;
      }

      const next = focusables[nextIndex];
      if (!next) return;
      event.preventDefault();
      try {
        next.focus({ preventScroll: true });
      } catch (e) {
        next.focus();
      }
    };

    this.addEventListener('focusin', onFocusIn);
    this.addEventListener('keydown', onKeyDown);
  }
}

customElements.define('hdt-hero-scrollstack', HeroScrollStack);

//Media with text scroll
class MediaTextScroll extends HTMLElement{
    constructor() {
        super();
        this._isFocusScrolling = false;
        this._focusScrollTimeout = null;
    }
    connectedCallback(){
        this._initScroll();
        this._onResize = () => this._initScroll();
        window.addEventListener('resize', this._onResize);
    }
    _initScroll(){
      if(window.matchMedia('(min-width: 768px)').matches){
        if (this.classList.contains('hdt-media-text--grid') || this.dataset.layout === 'grid') {
          this.classList.add('hdt-no-scroll');
          return;
        }

        const withContainer = $4('.hdt-slider__container', this).offsetWidth;
        const items = $$4('.hdt-ground-media-text .hdt-item-media-w-text', this)
        if (!items.length) return;
        const offset = items.length - 1
        const widthItems = items[0].offsetWidth;
        const gapHero = parseFloat(getComputedStyle($4('.hdt-ground-media-text', this)).columnGap);
        const totalW = widthItems * items.length + offset * gapHero

        this.classList.remove('hdt-no-scroll');

        if (totalW <= withContainer){
          this.classList.add('hdt-no-scroll');
          return;
        }
        const mediatextGroup = $4('.hdt-ground-media-text', this)

        if (this._isFocusScrolling) return;
        scroll(
            animate(mediatextGroup, {
                transform: ["none", `translateX(calc((${totalW}px - 100%) * -1 * var(--value-logical-flip)))`],
            }),
            { target: this }
        )

        this._bindFocusScroll();
      }
    }
    _bindFocusScroll(){
      const items = $$4('.hdt-ground-media-text .hdt-item-media-w-text', this);
      // const groupStack = $4('.hdt-ground-media-text', this);
      const prefersReduced = !matchMediaQuery("motion");
      if (!items.length) return;
      const getScrollTargetForIndex = (index) => {
        const rect = this.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const startY = window.scrollY + rect.top;
        const sectionHeight = rect.height;
        const scrollStart = startY;
        const scrollEnd = startY + sectionHeight - viewportHeight;
        if (scrollEnd <= scrollStart) {
          return scrollStart;
        }
        const maxStep = Math.max(items.length - 1, 1);
        let logicalIndex = index;
        const progress = maxStep === 0 ? 0 : logicalIndex / maxStep;
        return scrollStart + progress * (scrollEnd - scrollStart);
      };
      const handleFocusIn = (e) => {
        const target = e.target;
        if (!(target instanceof HTMLAnchorElement || target instanceof HTMLButtonElement)) return;
        const item = target.closest('.hdt-item-media-w-text');
        if (!item) return;
        const idx = items.indexOf(item)
        if (idx < 0) return;

        const rawTargetTop = getScrollTargetForIndex(idx);
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const maxScrollable = document.documentElement.scrollHeight - viewportHeight;
        const finalTop = Math.min( Math.max(0, rawTargetTop), Math.max(0, maxScrollable) );
        this._isFocusScrolling = true;
        clearTimeout(this._focusScrollTimeout);

        window.requestAnimationFrame(() => {
          window.scrollTo({ top: finalTop, behavior: prefersReduced ? 'auto' : 'smooth' });
        });

        this._focusScrollTimeout = setTimeout(() => {
          this._isFocusScrolling = false;
        }, prefersReduced ? 0 : 600);
      }
      this.addEventListener('focusin', handleFocusIn);
      //Disable tab defaault
      const focusableSelector = `.hdt-item-media-w-text button, .hdt-item-media-w-text [role="button"], .hdt-item-media-w-text a[href]:not([tabindex="-1"]), .hdt-item-media-w-text [tabindex]:not([tabindex="-1"])`;
      const getFocusableElements = () => Array.from($$4(focusableSelector, this)) .filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1);
      const handleKeyDown = (e) => {
        if (e.key !== 'Tab' || e.defaultPrevented) return;
        const active = document.activeElement;
        if (!active || !this.contains(active)) return;
        const focusables = getFocusableElements();
        if (!focusables.length) return;
        const currentIndex = focusables.indexOf(active);
        const dir = e.shiftKey ? -1 : 1;
        let nextIndex;
        if (currentIndex === -1) {
          nextIndex = dir > 0 ? 0 : focusables.length - 1;
        } else {
          nextIndex = currentIndex + dir;
        }

        if (nextIndex < 0 || nextIndex >= focusables.length) {
          return;
        }

        const next = focusables[nextIndex];
        if (!next) return;
        e.preventDefault();
        try {
          next.focus({ preventScroll: true });
        } catch (e) {
          next.focus();
        }
      };
      this.addEventListener('keydown', handleKeyDown);
    }

    disconnectedCallback(){
      window.removeEventListener('resize', this._onResize);
    }
}
customElements.define("hdt-mediatext-scroll", MediaTextScroll);

//Media lookbook
class MediaLookbook extends HTMLElement{
    constructor() {
        super();
        this.initialized = false;
        this.tabPlayer = $4("hdt-tab-player-list", this);
        this.buttons = $$4('button', this.tabPlayer)
        // this.prevIndex = this.tabPlayer.currIndex;
        this.prevIndex = this.getActiveIndex();

    }
    connectedCallback(){
      this.pauseOnhover();
      if (this.hasAttribute("reveal-on-scroll")) {
        this.animateOnScrollOnce();
      }
      this.observeTabChange();
      if (window.Shopify && Shopify.designMode) {
        this._setupShopifyBlockSelection();
      }
    }
    getActiveIndex() {
      return [...this.buttons].findIndex(
        btn => btn.getAttribute('aria-current') === 'true'
      );
    }
    observeTabChange() {
      this.mo = new MutationObserver(() => {
        const currIndex = this.getActiveIndex();
        if (currIndex !== -1 && currIndex !== this.prevIndex) {
          this.onTabChange(currIndex, this.prevIndex);
          this.prevIndex = currIndex;
        }
      });

      this.buttons.forEach(btn => {
        this.mo.observe(btn, { attributes: true, attributeFilter: ['aria-current'] });
      });

    }
    onTabChange(currIndex, prevIndex = null) {
      const tabContents = $$4(".hdt-tab-content-wrapper > .hdt-tab-content", this);
      const tabContentShow = tabContents[currIndex];
      const tabMedias = $$4(".hdt-tab-image-holder .hdt-lookbook-media", this);
      const tabMediaShow = $4('.hdt-lb-image', tabMedias[currIndex]);
      const pinItems = $$4(".hdt-pin-item", tabMedias[currIndex]);
      let sequence
      if(prevIndex == null){
        sequence = [
          [ tabContentShow, { opacity: [0,1], y: [30, 0] }, { duration: 0.4, at:"-0.2" } ],
          [ tabMediaShow, { '--inset-right': ['100%', '0%'] }, { duration: 0.4, at: "<" }],
          //[ tabMediaShow, { clipPath: [ "inset(0 100% 0 0 round var(--rounded-lg))", "inset(0 0 0 0 round var(--rounded-lg))"] }, { duration: 0.4, at:"<" } ],
          [ pinItems,   { opacity: [0,1], scale: [0.8,1] }, { duration: 0.5, delay: stagger(0.3) } ]
        ];
      }else{
        const tabContentHide = tabContents[prevIndex];
        sequence = [
          [ tabContentHide, { opacity: [1,0], y: [0, 30] }, { duration: 0.4 } ],
          [ tabContentShow, { opacity: [0,1], y: [30, 0] }, { duration: 0.4, at:"-0.2" } ],
          [ tabMediaShow, { '--inset-right': ['100%', '0%'] }, { duration: 0.4, at: "<" }],
          //[ tabMediaShow, { clipPath: [ "inset(0 100% 0 0 round var(--rounded-lg))", "inset(0 0 0 0 round var(--rounded-lg))"] }, { duration: 0.4, at:"<" } ],
          [ pinItems,   { opacity: [0,1], scale: [0.8,1] }, { duration: 0.5, delay: stagger(0.3) } ]
        ];
      }
      return animate(sequence);
    }

    _setupShopifyBlockSelection() {
      document.addEventListener(admEvts.select, event => {
        const block = event.target;
        if (block.classList.contains('hdt-tab-title')) {
          setTimeout(() => {
            block.click();
          }, 20);
        }
      });
    }

    animateOnScrollOnce(){
        inView(this, () => {
            animate(
                this.buttons,
                { opacity: [0, 1], x: [-30, 0] },
                { duration: 0.3, delay: stagger(0.2) }
            );
            this.onTabChange(this.prevIndex);
        }, { amount: 0.3 })
    }

    pauseOnhover(){
        const tabContent = $4('.hdt-tab-image-holder', this)
        const tabPrContent = $4('.hdt-tab-content-wrapper', this)
        const listTab = $4('hdt-tab-player-list', this)
        if(listTab.hasAttribute("autoplay")){
            const stopOnHover = (el) => {
                el?.addEventListener('mouseenter', () => {
                    listTab.pause();
                });
                el?.addEventListener('mouseleave', () => {
                    listTab.play();
                });
            };
            stopOnHover(tabContent);
            stopOnHover(tabPrContent);
        }
    }
}
customElements.define("hdt-media-lookbook", MediaLookbook);

// Sidebar fillter
class MainCollection extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
    }
    connectedCallback() {
      this.bindFilterButton();
      this.observeDOMChanges();
    }
    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
    bindFilterButton() {
      const btn = $4('.hdt-facets__open', this);
      if (!btn || btn.dataset.bound === 'true') return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', (e) => {
        const id = btn.getAttribute('aria-controls');
        const isExpanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', !isExpanded);
        const target = $id4(id);
        if (target) {
          target.classList.toggle('hdt-stage-open');
        }
      });
    }
    observeDOMChanges() {
      this.observer = new MutationObserver(() => {
        this.bindFilterButton();
      });

      this.observer.observe(this, {
        childList: true,
        subtree: true
      });
    }
}
customElements.define('hdt-main-collection', MainCollection);

//Footer Clip-path
class MorphFooter extends HTMLElement {
  #scroll;
  connectedCallback() {
    const logoFooter = $4('.hdt-footer__logo', this);
    if (!this.hasAttribute('reveal-on-scroll') || !matchMediaQuery("motion") || matchMediaQuery("mobile") || !logoFooter) return;
    const opacity   = motionValue(0),
          x         = motionValue('-100%'),
          logoStart = 0.1;
    styleEffect(logoFooter, { opacity, x });

    this.#scroll = scroll((progress) => {
      const logoProgress = Math.max(0, (progress - logoStart) / (1 - logoStart));
      opacity.set(logoProgress);
      x.set(`${(1 - logoProgress) * -100}%`);
    },
    {
      target: this,
      offset: ["start end", "end end"]
    });
  }
  disconnectedCallback() {
    if (this.#scroll) this.#scroll();
  }
}
customElements.define("hdt-morph-footer", MorphFooter);

//Collection list dynamic
class CollectionDynamic extends HTMLElement {
  #controller;
  connectedCallback() {
    this.#controller = new AbortController();
    const slider = $4('hdt-slider', this),
    { signal } = this.#controller;
    if (slider.apiS) this.#fn(slider.apiS);
    slider?.addEventListener('reInit', (event) => this.#fn(event.detail.sliderApi), { signal });
    slider?.addEventListener('select', (event) => this.#fn(event.detail.sliderApi), { signal });
  }
  disconnectedCallback() {
    this.#controller?.abort();
  }
  #fn(sliderApi) {
    this.classList.toggle('hdt-can-slide-prev', sliderApi.canScrollPrev());
    this.classList.toggle('hdt-can-slide-next', sliderApi.canScrollNext());
  }
}
customElements.define("hdt-collection-dynamic", CollectionDynamic);

const inset = 'inset(0 round 0px)';
class scrollMediaText extends HTMLElement {
    #scroll;
    connectedCallback(){
        const media = $4('.hdt-scroll-media-with-text__media .hdt-media-wrapper', this),
        sequence = [
          //[$4('.hdt-scroll-media-with-text__media .hdt-media-wrapper', this), { width: [fixedStart, width, width, width], height: [fixedStart, height, height, height], borderRadius: [120, 0, 0, 0] }]
          [media, { clipPath: ["inset(calc(50% - var(--media-radius)) calc(50% - var(--media-radius)) round var(--media-radius))", inset, inset, inset]}],
          [$4(':where(hdt-video, svg, img)', media), { scale: [0.5, 1, 1, 1]}, { at: "<" }]
        ];
        const content = $4('.hdt-scroll-media-with-text__content', this);
        if (content) {
          sequence.push(
            [content, { opacity: [0, 0, 1, 1], y: [255, 255, 0, 0] }, { at: "<" }]
          )
        }
        this.#scroll = scroll(
          animate(sequence),
          {
              target: this.parentElement,
              offset: ["start start", "0.4 start", "0.5 start", "end start"],
          }
        );
    }
    disconnectedCallback() {
      this.#scroll();
    }
}
customElements.define("hdt-scroll-media-text", scrollMediaText);

const inset2 = "inset(100% round 0px)";
class scrollMediaHero extends HTMLElement {
  connectedCallback(){
    const sequence = [],
    is3 = "inset(50% round 0px)",
    is4 = "inset(calc(50% - 100px) calc(50% - 100px) round 100px",
    content = $4('.hdt-scroll-hero-media__first .hdt-scroll-hero-media__content', this);
    if (content) {
      sequence.push( [content, { opacity: [0, 1, 1, 1, 0, 0, 0], y: [200, 0, 0, 0, 0, 0, 0], scale: [0.6, 1, 1.05] }] )
    }
    const items = $$4('.hdt-scroll-hero-media__item', this);
    if (items) {
      let translate = "translate(calc(-1 * var(--left-start)), calc(-1 * var(--top-start))) scale(0.5)",
      translate2 = "translate( calc(var(--tx-end1, 0) - var(--tx-start1) - (1% * var(--tx-end))), calc(var(--ty-end1, 0) - var(--ty-start1) - (1% * var(--ty-end))) )";
      sequence.push(
        [items, {
          opacity: [0, 1, 0, 0, 0, 0, 0, 0],
          //transform: [translate, `${translate2} scale(2)`, `${translate2} scale(2)`, `${translate2} scale(2)`, `${translate2} scale(2)`, `${translate2} scale(2.5)`, `${translate2} scale(3)`]
          transform: [translate, `${translate2} scale(1)`, `${translate2} scale(0.8)`, `${translate2} scale(0.5)`, `${translate2} scale(0.5)`, `${translate2} scale(0.5)`, `${translate2} scale(0.5)`]
        }, { at: "<", delay: stagger(0.015) }, { offset: ["start start", "0.15 start", "0.2 start", "0.4 start"] }]
      );
    }
    const second = $4('.hdt-scroll-hero-media__second', this);
    if (second) {
      sequence.push( [second, { clipPath: [inset2, is3, is4, inset, inset, inset] }, { at: "<", delay: 0.08 }] )
    }
    const content2 = $4('.hdt-scroll-hero-media__content', second);
    if (content2) {
      sequence.push( [content2, { opacity: [0, 0, 0, 0, 1, 1, 1], y: [200, 200, 200, 0, 0, 0], scale: [0.4, 1, 1] }, { at: "<" }] )
    }
    scroll( animate(sequence), { target: this.parentElement, offset: ["start start", "0.12 start", "0.33 start", "0.5 start", "0.8 start", "0.88 start", "end start"], } );
    const first = $4('.hdt-scroll-hero-media__media', this);
    scroll( animate(first, { transform: [`scale(1)`, `scale(1.15)`] }), { target: this.parentElement,  offset: ["start end", "0.5 start"], } );
    if (window.Shopify && Shopify.designMode) {
      this._setupShopifyBlockSelection();
    }
  }
  _setupShopifyBlockSelection() {
    document.addEventListener(admEvts.select, event => {
      event.target.classList.add('hdt-current-block');
    });
    document.addEventListener(admEvts.deselect, event => {
      event.target.classList.remove('hdt-current-block');
    });
  }
}
customElements.define("hdt-scroll-hero-media", scrollMediaHero);

class sliderPlay extends HTMLElement {
  #id;
  #controller;
  constructor(){
    super();
    this.#id = $4('button', this).getAttribute("aria-controls");
  }
  connectedCallback() {
    this.#controller = new AbortController();
    if (!this.slider || !this.slider.interval) return;
    const { signal } = this.#controller;

    this.slider.addEventListener('reInit', this.#resetProgress.bind(this), { signal });
    this.slider.addEventListener('select', this.#resetProgress.bind(this), { signal });
    this.slider.addEventListener('play:state', () => this.toggleAttribute('playing', this.slider.isPlaying), { signal });
    this.addEventListener('click', this.#tooglePlay.bind(this), { signal });
    this.slider.addEventListener('mousedown', () => this.slider.isMousedown = true, { signal });
    this.slider.addEventListener('mouseup', () => this.slider.isMousedown = false, { signal });

  }
  disconnectedCallback() {
    this.#controller.abort();
  }
  #resetProgress(event) {
    const { slideNodes, selectedScrollSnap } = event.detail.sliderApi;
    this.parentElement.setAttribute('color-scheme', slideNodes()[selectedScrollSnap()].getAttribute('color-scheme'));

    this.removeAttribute('running');
    this.toggleAttribute('playing', this.slider.isPlaying);
    frame.render(() => {
      this.setAttribute('running', '');
    })
  }
  #tooglePlay() {
    if (this.slider.isPlaying) {
      this.slider.pause();
    } else {
      this.slider.play();
    }
  }
  get slider() {
    return $id4(this.#id);
  }
}
customElements.define("hdt-slider-play", sliderPlay);

class DrawerHotspots extends HTMLElement {
  #controller;
  #needUpdate = false;
  #index = -1;
  #preBtn;
  #nextBtn;
  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const tooltip = $4('.hdt-tooltip-des', this);
    this.addEventListener(dialogAdded, ()=> {
      const drawer = $4('hdt-drawer', this),
      slider = $4('hdt-slider', this),
      { dialog } = drawer;
      this.#preBtn = $4('button[name="previous"]', drawer);
      this.#nextBtn = $4('button[name="next"]', drawer);
      if (!this.#preBtn) return;
      dialog?.addEventListener(dialogOpening, ()=> {
        this.#needUpdate = true;
        this.setAttribute('open', '');
      }, { signal });
      dialog?.addEventListener(dialogClose, ()=> {
        this.#needUpdate = false;
        this.removeAttribute('open');
      }, { signal });

      slider?.addEventListener('reInit', (event) => {
        if (!this.#needUpdate) return;
        slider.goToIndex(parseInt(dialog.btnOpening.dataset.index) || 0, true);
        this.#updateNavHeading(event);
      }, { signal });
      slider?.addEventListener('select', this.#updateNavHeading, { signal });
    }, { once: true, signal });
    if (!Shopify.designMode) return;
      const m_x     = motionValue(0),
            m_y     = motionValue(0),
            img     = $4('.hdt-hotspots-with-drawer__hotspos-wrap', this),
            left    = transformValue(() => `${m_x.get()}px`),
            top     = transformValue(() => `${m_y.get()}px`);
      styleEffect(tooltip, { left, top });
      img.addEventListener("mousemove", (e) => {
        const rect = img.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        tooltip.style.opacity = this.hasAttribute('open') ? "0" : "1";
        const percentX = ((x / rect.width) * 100).toFixed(0);
        const percentY = ((y / rect.height) * 100).toFixed(0);
        tooltip.textContent = `X: ${percentX}% Y: ${percentY}%`;
        m_x.set(e.clientX);
        m_y.set(e.clientY);
      }, { signal });
      const cancelHover = hover(img, () => {
        tooltip.style.display = "block";
        tooltip.style.opacity = "1";
        return () => {
          tooltip.style.opacity = "0";
          tooltip.style.display = "none";
        }
      });
      signal.addEventListener('abort', () => cancelHover(), { once: true });
  }
  disconnectedCallback() {
    this.#controller.abort();
  }
  #updateNavHeading = (event) => {
    if (!this.#needUpdate && event.detail?._getSliderApi) return;
    const { sliderApi } = event.detail,
    index = sliderApi.selectedScrollSnap();

    if (this.#index == index) return;
    const slideNodes = sliderApi.slideNodes(),
    indexPrev = wrap(0, slideNodes.length, index - 1),
    indexNext = wrap(0, slideNodes.length, index + 1);
    // update prev heading
    this.#preBtn.nextElementSibling.textContent = $4('.hdt-hs-drawer-heading', slideNodes[indexPrev])?.textContent || '';
    // update next heading
    this.#nextBtn.nextElementSibling.textContent = $4('.hdt-hs-drawer-heading', slideNodes[indexNext])?.textContent || '';
    // cache current index
    this.#index = index;
  }
}

customElements.define("hdt-hotspots-with-drawer", DrawerHotspots);
class Floating extends HTMLElement {
  #controller;
  constructor() {
    super();
  }
  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller,
    reference        = this.previousElementSibling;

    computePosition(reference, this, {
      placement: this.#placement,
      middleware: [
        flip(),
        //offset(20),
        offset({
          mainAxis: 12,
          crossAxis: 1
        }),
        shift({padding: 1}),
        this.#arrowEl && arrow({ element: this.#arrowEl })
      ]
    }).then(({ x, y, placement, middlewareData }) => {
      Object.assign(this.style, {
        top: `${y}px`,
        left: `${x}px`,
      });
      if (middlewareData.arrow) {
        this.setAttribute('placement-current', placement);
        // Accessing the data
        const {x: arrowX, y: arrowY} = middlewareData.arrow;
        const staticSide = {
          top: 'bottom',
          right: 'left',
          bottom: 'top',
          left: 'right',
        }[placement.split('-')[0]];
        Object.assign(this.#arrowEl.style, {
          left: arrowX != null ? `${arrowX}px` : '',
          top: arrowY != null ? `${arrowY}px` : '',
          right: '',
          bottom: '',
          [staticSide]: '-4px',
        });
      }
    });
    const cancelHover = hover(reference, () => {
      this.setAttribute('open', '');
      return () => this.removeAttribute('open');
    })
    signal.addEventListener('abort', () => cancelHover(), { once: true });
  }
  disconnectedCallback() {
    this.#controller.abort();
  }
  get #arrowEl() {
    return this._arrowEl ??= $4('.hdt-floating__arrow', this);
  }
  get #placement() {
    return this.getAttribute("placement") || 'top';
  }
}
customElements.define("hdt-floating", Floating);

// Volume pricing
class VolumePricing extends HTMLElement{
  #abortController;
  connectedCallback(){
    this.#abortController = new AbortController();
    $4('[ref="volumePricingToggle"]', this)?.addEventListener('click', () => this.classList.toggle('hdt-volume-pricing--expanded'), { signal: this.#abortController.signal });
  }
  disconnectedCallback() {
    this.#abortController?.abort();
  }
}
customElements.define("hdt-volume-pricing", VolumePricing);

//Per price
class PricePerItemComponent extends HTMLElement {
  #priceBreaks = [];
  #quantityInput = null;
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#parsePriceBreaks();
    this.#findQuantityInput();
    const { signal } = this.#abortController;
    this.#quantityInput?.addEventListener('input', () => this.#updatePriceDisplay(), { signal });
    this.#quantityInput?.addEventListener('change', () => this.#updatePriceDisplay(), { signal });
    //this.#updatePriceDisplay();
  }
  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #parsePriceBreaks() {
    // const minQuantity = parseInt(this.dataset.minQuantity || '') || 1;
    // const { variantPrice, priceBreaks } = this.dataset;
    const { priceBreaks } = this.dataset;
    
    // Start with base price tier
    // if (variantPrice) {
    //   this.#priceBreaks.push({ quantity: minQuantity, price: variantPrice });
    // }

    if (priceBreaks) {
      try {
        const breaks = JSON.parse(priceBreaks);
        for (const { quantity, price } of breaks) {
          if (quantity && price) {
            this.#priceBreaks.push({ quantity: parseInt(quantity), price });
          }
        }
      } catch (error) {
        console.warn('[price-per-item] Failed to parse data-price-breaks: ', error);
      }
    }
    
    // Sort by quantity descending for efficient lookup
    this.#priceBreaks.sort((a, b) => b.quantity - a.quantity);
  }

  /** * Find input[name="quantity"] in <product-form> */
  #findQuantityInput() {
    const form = this.closest('product-form');
    if (!form) return;
    this.#quantityInput = $4('input[name="quantity"]', form);

    if (!this.#quantityInput) {
      console.warn('[price-per-item] Could not find input[name="quantity"] inside <product-form>.');
    }
  }

  /** * Gets the total quantity (cart + current input value)  */
  #getCurrentQuantity() {
    if (!this.#quantityInput) return 1;

    const cartQuantity = parseInt(this.#quantityInput.dataset.cartQuantity || '0', 10) || 0;
    const inputQuantity = parseInt(this.#quantityInput.value, 10) || 1;

    return cartQuantity + inputQuantity;
  }

  // Price breaks are sorted descending, find first tier that quantity qualifies for
  #updatePriceDisplay() {
    if (!this.firstElementChild || this.#priceBreaks.length === 0 || !this.#quantityInput.checkValidity()) return;
    const quantity = this.#getCurrentQuantity();
    //console.log(quantity)
    const matchedBreak = this.#priceBreaks.find((priceBreak) => quantity >= priceBreak.quantity) ?? this.#priceBreaks[this.#priceBreaks.length - 1];
    this.firstElementChild.textContent = matchedBreak.price;
  }
}
customElements.define('price-per-item', PricePerItemComponent);

class ProductBundle extends HTMLElement {
  #min;
  #max;
 #add;
  #addBundles;
  #tmp;
  #items;
  #bar;
  #formData = { items: [], sections: [] };
  #total = 0;
  #totalEls;
  #countAdded = 0;
  #abortController;
  #error;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.#min = parseInt(this.getAttribute('min'));
    this.#max = parseInt(this.getAttribute('max'));
    this.#add = $4('[ref="addBundleToCart"]', this);
    this.#addBundles = $$4('[ref="addBundle"]', this);
    this.#tmp = $4('[ref="tmp"]', this);
    this.#items = $4('[ref="items"]', this);
    this.#bar = $4('[ref="bar"]', this);
    this.#totalEls = $$4('[ref="bundleTotal"]', this);
    this.#error = $4('[ref="addBundleError"]', this);

    let isShow = false;
    $4('[ref="expandButton"]', this).addEventListener( 'click', () => {
      isShow = !isShow;
      this.toggleAttribute('expanded', isShow);
    }, { signal });

    // add/remove item bundle
    this.addEventListener( 'click', (e) => {
      const btn = e.target,
      ref = btn.getAttribute('ref');
      let updateTotal = false
      if (ref == 'addBundle') {
        this.#addBundle(btn);
        updateTotal = true;
      } else if (ref == 'removeBundle') {
        this.#removeBundle(btn);
        updateTotal = true;
      }
      if (updateTotal) {
        this.#totalEls.forEach( (el) => el.textContent = currency.formatMoney(this.#total) )
      }
    }, { signal });

    this.#add.addEventListener('click', (e) => {
      this.#formData.sections = this.#cart?.sectionsToRender?.map(section => section.id) ?? ['cart-json'];
      this.#add.setAttribute("aria-disabled", true);
      this.#add.setAttribute("aria-busy", true);
      document.dispatchEvent(new CustomEvent(loadingStart));
      fetch(Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.#formData)
      })
      .then(response => {
        this.#add.removeAttribute("aria-disabled");
        this.#add.removeAttribute("aria-busy");
        document.dispatchEvent(new CustomEvent(loadingEnd));
        return response.json();
      }).then(data => {
        if (data.status) {
          //throw new Error(`error: ${data}`);
          if (data.description) {
            this.#error.lastElementChild.textContent = data.description;
            this.#error.hidden = false;
          }
          this.#removeAllBundle();
          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart reload event
          // because our back-end still adds the max allowed amount to the cart.
          document.dispatchEvent(new CustomEvent(cartReload, {
            bubbles: true
          }));
          return;
        } else {
          this.#removeAllBundle();
        }
        const cartData = {
          ...data,
          ...JSON.parse(data?.sections?.['cart-json']?.split('[split_94]')?.[1] || '{}')
        };
        this.dispatchEvent(new CustomEvent(cartUpdate, {
          bubbles: true,
          detail: {
            source: 'product-bundle',
            actionAfterATC: themeHDN.settings.actionAfterATC,
            cartData
          }
        }));
      })
      .catch((error) => {
        console.error('Error:', error);
      });
    }, { signal });

    this.addEventListener("richlist:select", (e) => {
      const btn = e.target;
      if (!btn.hasAttribute('data-price')) return;

      // 1. Update JSON
      const product = btn.closest('.hdt-card-product'),
      scriptEl = $4('[ref="json"]', product),
      newData = JSON.parse(JSON.stringify(btn.dataset));
      newData.id = btn.value;
      newData.options = btn.textContent;
      this.#updateJson(scriptEl, newData);
      
      // 2. Update DOM
        // 2.1. Img
        if (newData.src) {
          const mediaMain = $4('.hdt-card-product__media--main', product),
          newImg = this.#createImg(renderSrc(newData.src, newData.width), mediaMain);
          mediaMain.replaceWith(newImg);
        }

        // 2.2. Price
        const priceEl = $4('[ref="price"]', product),
        comparePriceEl = $4('[ref="compare-price"]', product),
        { price, compareprice } = newData;
        if (priceEl) {
          priceEl.textContent = currency.formatMoney(price);
          comparePriceEl.parentElement.hidden = compareprice <= price;
          if (compareprice > price) comparePriceEl.textContent = currency.formatMoney(compareprice);
        }

    }, { signal });
  }
  disconnectedCallback() {
    this.#abortController?.abort();
  }
  #addBundle(btn) {
    this.#error.hidden = true;
    ++this.#countAdded;
    if (this.#countAdded >= this.#min) this.#add.removeAttribute('disabled');
    if (this.#countAdded <= this.#min) {
      this.#slot(this.#min - this.#countAdded + 1).hidden = true;
      if (this.#bar) this.#bar.value = this.#countAdded;
    }
    if (btn.hasAttribute('add-once')) {
      btn.setAttribute('disabled', '');
      btn.setAttribute('added', '')
    }
    if (this.#countAdded >= this.#max) this.#addBundles.forEach((btn) => btn.setAttribute('disabled', ''));
    const dataItem = JSON.parse(btn.previousElementSibling.textContent.trim()),
    newItem = this.#createBundleItem(dataItem, btn.dataset.prId);
    this.#addItem(this.#formData.items, { 'id': dataItem.id, 'quantity': dataItem.min });
    this.#total += dataItem.price * dataItem.min;
    this.#items.prepend(newItem);
  }
  #removeBundle(btn) {
    --this.#countAdded;
    if (this.#countAdded < this.#min) {
      this.#add.setAttribute('disabled', '');
      this.#slot(this.#min - this.#countAdded).hidden = false;
      if (this.#bar) this.#bar.value = this.#countAdded;
    }
    this.#removeItem(this.#formData.items, { 'id': btn.dataset.id, 'quantity': btn.dataset.min });
    this.#total -= parseInt(btn.dataset.price);
    $4(`[ref="addBundle"][data-pr-id="${btn.dataset.prId}"]`)?.removeAttribute('added');
    this.#addBundles.forEach((_btn) => _btn.toggleAttribute('disabled', _btn.hasAttribute('added')));
    btn.closest('[ref="item"]').remove();
  }
  #createBundleItem(data, prId) {
    const clone = this.#tmp.content.cloneNode(true),
    imgEl = $4('[ref="img"]', clone),
    titleEl = $4('[ref="title"]', clone),
    optionsEl = $4('[ref="options"]', clone),
    moneyEl = $4('[ref="money"]', clone),
    removeEl = $4('[ref="removeBundle"]', clone),
    removeTextEl = $4('[ref="removeText"]', removeEl);

    if (imgEl) {
      imgEl.src = data.src || data.prSrc;
      imgEl.setAttribute('height', data.height || data.prHeight);
      imgEl.alt = data.title;
    }

    titleEl.textContent = data.title;
    moneyEl.textContent = currency.formatMoney(data.price);

    if (data.options) {
      optionsEl.textContent = data.options;
    } else {
      optionsEl.hidden = true;
    }
    removeEl.dataset.id = data.id;
    removeEl.dataset.prId = prId;
    removeEl.dataset.min = data.min;
    removeEl.dataset.price = data.price * data.min;
    removeTextEl.textContent = removeTextEl.textContent.replace('title94', data.title);

    return clone;
  }
  #slot(index) {
    return $4(`.hdt-bundle-slot-skeleton[slot="${index}"]`, this);
  }
  // Helper function: Checks if two items match in all key attributes (id, selling_plan, parent_id)
  #isSameItem(itemA, itemB) {
    return (
      itemA.id === itemB.id &&
      (itemA.selling_plan || null) === (itemB.selling_plan || null) &&
      (itemA.parent_id || null) === (itemB.parent_id || null)
    );
  }
  // Logic: ADD item (Increase quantity if item exists, otherwise add new item)
  #addItem(itemsList, newItem) {
    const addQty = newItem.quantity || 1;

    // Find if the item already exists in the list
    const existingItem = itemsList.find(item => this.#isSameItem(item, newItem));

    if (existingItem) {
      // Item exists -> Increase quantity
      existingItem.quantity += addQty;
    } else {
      // Item does not exist -> Push new item
      itemsList.push({ ...newItem, quantity: addQty });
    }

    return itemsList;
  }
  #removeItem(itemsList, targetItem) {
    const removeQty = targetItem.quantity || 1;

    // Find the index of the item
    const index = itemsList.findIndex(item => this.#isSameItem(item, targetItem));

    if (index !== -1) {
      // Decrease quantity
      itemsList[index].quantity -= removeQty;

      // Remove from array if quantity drops to 0 or below
      if (itemsList[index].quantity <= 0) {
        itemsList.splice(index, 1);
      }
    }

    return itemsList;
  }
  get #cart() {
    return $4('[ref="hdt-cart"]');
  }
  #updateJson(scriptEl, newData) {
    const currentData = JSON.parse(scriptEl.textContent);
    const updatedData = { ...currentData, ...newData };
    //console.log(currentData, newData, updatedData)
    scriptEl.textContent = JSON.stringify(updatedData);
    return updatedData;
  }
  #createImg(vimg, mediaMain) {
    return createImg({
      src: vimg,
      width: mediaMain.getAttribute('width'),
      height: mediaMain.getAttribute('height'),
      alt: mediaMain.alt,
      onload: true,
      attrs: {
        class: mediaMain.className,
        sizes: mediaMain.sizes,
        'data-widths': mediaMain.getAttribute('data-widths') || '[]'
      }
    })
  }
  #removeAllBundle() {
    // 1. Reset btn add bundle
    this.#addBundles.forEach((btn) => btn.removeAttribute('disabled'));

    // 2. Reset total price
    this.#totalEls.forEach( (el) => el.textContent = currency.formatMoney(0) );

    // 3. Reset items
    Array.from(this.#items.children).forEach((child) => {
      if (child.hasAttribute('slot')) {
        child.hidden = false;
      } else {
        child.remove();
      }
    });

    // 4. Reset btn add bundle to cart
    this.#add.setAttribute('disabled', '');

    // 5. Reset bar
    if (this.#bar) this.#bar.value = 0;

    // 6. Reset #formData
    this.#formData.items = [];

    // 7. Reset total price & countAdded
    this.#total = 0;
    this.#countAdded = 0;
  }
}
customElements.define('hdt-product-bundle', ProductBundle);

class scrollSentinel extends HTMLElement {
  #page = 1;
  #totalPages;
  #popover;
  #observer;
  #isLoading = false;
  constructor() {
    super();
    this.#totalPages = Math.ceil(parseInt(this.dataset.total) / 250);
    this.#popover = this.closest('.hdt-popover');
    this.#popover.addEventListener(dialogOpen, () => {
      if (!this.#observer) this.#setupObserver();
    }, { once: true });
  }
  #setupObserver() {
    this.#observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !this.#isLoading) this.#fetchNext();
    }, {
      root: this.closest('.hdt-popover__inner'),
      threshold: 0,
      rootMargin: '600px'
    });
    this.#observer.observe(this);
  }
  #destroyObserver() {
    this.#observer.disconnect();
    this.#observer = null;
    this.hidden = true;
  }
  async #fetchNext() {
    this.isLoading = true;
    this.#popover.setAttribute('loading', '');
    const response = await fetch(`${this.dataset.url}?page=${this.#page}&section_id=options`);

    this.#isLoading = false;
    this.#popover.removeAttribute('loading');
    if (!response.ok) {
      throw new Error(`Failed to load page ${this.#page}}`);
    }

    if (this.#page >= this.#totalPages) {
      this.#destroyObserver();
    } else {
      this.#page++;
    }
    const htmlText = await response.text(),
    html = new DOMParser().parseFromString(htmlText,'text/html');
    requestAnimationFrame(() => {
      this.insertAdjacentHTML('beforebegin', html.getElementById('shopify-section-options').innerHTML);
    });
  }
}
customElements.define('hdt-scroll-sentinel', scrollSentinel);

class VideoCarousel extends HTMLElement {
  #controller;
  #unobserve;
  #cards = [];
  #currentIndex = 0;
  #inView = false;
  #userPaused = false;
  #muted = true;
  #dragging = false;
  #pointer = { x: 0, y: 0 };

  connectedCallback() {
    this.#controller = new AbortController();
    this.#cards = $$4('[data-video-card]', this);
    if (!this.#cards.some((card) => $4('video', card))) return;

    this.#muted = this.hasAttribute('data-muted');
    this.#cards.forEach((card, index) => this.#setupCard(card, index));
    this.#syncMuteUI();

    const { signal } = this.#controller;
    this.#unobserve = inView(
      this,
      () => {
        this.#onEnterView();
        return () => this.#onLeaveView();
      },
      { amount: 0.25 }
    );

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) {
          this.#pauseCurrent();
        } else if (this.#inView && !this.#userPaused) {
          this.#playIndex(this.#currentIndex, { resume: true });
        }
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#unobserve?.();
    this.#controller?.abort();
    this.#cards.forEach((card) => this.#video(card)?.pause());
  }

  get slider() {
    return this._slider ??= $4('hdt-slider-reveal, hdt-slider', this);
  }

  #video(card) {
    return $4('video', card);
  }

  #setupCard(card, index) {
    const { signal } = this.#controller;
    const video = this.#video(card);
    if (!video) return;
    video.loop = false;
    video.controls = false;
    video.playsInline = true;
    video.muted = this.#muted;
    video.preload = index === 0 ? 'metadata' : 'none';

    video.addEventListener(
      'ended',
      () => {
        if (this.#userPaused || !this.#inView) return;
        if (this.#currentIndex !== index) return;
        const next = (index + 1) % this.#cards.length;
        this.#playIndex(next, { fromEnded: true });
      },
      { signal }
    );

    card.addEventListener(
      'pointerdown',
      (event) => {
        this.#dragging = false;
        this.#pointer.x = event.clientX;
        this.#pointer.y = event.clientY;
      },
      { signal }
    );

    card.addEventListener(
      'pointermove',
      (event) => {
        if (this.#dragging) return;
        const dx = event.clientX - this.#pointer.x;
        const dy = event.clientY - this.#pointer.y;
        if (Math.hypot(dx, dy) > 8) this.#dragging = true;
      },
      { signal }
    );

    card.addEventListener(
      'click',
      (event) => {
        if (this.#dragging) return;
        if (event.target.closest('a')) return;
        if (event.target.closest('[data-video-mute]')) {
          event.preventDefault();
          event.stopPropagation();
          this.#toggleMute();
          return;
        }

        const isPlaying = card.classList.contains('is-playing');
        if (isPlaying) {
          this.#pauseCurrent({ user: true });
          return;
        }

        if (event.target.closest('[data-video-play]')) {
          event.preventDefault();
          this.#playIndex(index);
        }
      },
      { signal }
    );
  }

  #onEnterView() {
    this.#inView = true;
    if (this.#userPaused) return;
    this.#playIndex(this.#currentIndex, { resume: true });
  }

  #onLeaveView() {
    this.#inView = false;
    this.#pauseCurrent();
  }

  #playIndex(index, { resume = false, fromEnded = false } = {}, hops = 0) {
    if (hops > this.#cards.length) return;
    const card = this.#cards[index];
    const video = this.#video(card);
    if (!card || !video) {
      const next = (index + 1) % this.#cards.length;
      if (next !== index) this.#playIndex(next, { resume, fromEnded }, hops + 1);
      return;
    }

    const wrapping = fromEnded && index === 0 && this.#cards.length > 1;
    this.#userPaused = false;
    this.#currentIndex = index;
    this.#cards.forEach((item, i) => {
      if (i === index) return;
      const other = this.#video(item);
      if (other && !other.paused) other.pause();
      item.classList.remove('is-playing');
    });

    if (wrapping || !this.#isSlideInView(index)) {
      this.#scrollToIndex(index);
    }

    if (!resume || video.ended) {
      try {
        video.currentTime = 0;
      } catch (error) {}
    }

    video.preload = 'auto';
    video.muted = this.#muted;
    card.classList.add('has-started', 'is-playing');
    this.#syncMuteUI();

    const playPromise = video.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {
        if (!this.#muted) {
          this.#muted = true;
          video.muted = true;
          this.#syncMuteUI();
          video.play().catch(() => {
            card.classList.remove('is-playing');
          });
        } else {
          card.classList.remove('is-playing');
        }
      });
    }
  }

  #pauseCurrent({ user = false } = {}) {
    const card = this.#cards[this.#currentIndex];
    const video = this.#video(card);
    if (video && !video.paused) video.pause();
    card?.classList.remove('is-playing');
    if (user) this.#userPaused = true;
  }

  #toggleMute() {
    this.#muted = !this.#muted;
    this.toggleAttribute('data-muted', this.#muted);
    this.#cards.forEach((card) => {
      const video = this.#video(card);
      if (video) video.muted = this.#muted;
    });
    this.#syncMuteUI();
  }

  #syncMuteUI() {
    this.#cards.forEach((card) => {
      card.classList.toggle('is-muted', this.#muted);
      const btn = $4('[data-video-mute]', card);
      if (!btn) return;
      btn.setAttribute(
        'aria-label',
        this.#muted ? btn.dataset.unmuteLabel || '' : btn.dataset.muteLabel || ''
      );
    });
  }

  #isSlideInView(index) {
    const slide = this.#cards[index]?.closest('.hdt-slider__slide');
    const viewport = $4('.hdt-slider__viewport', this);
    if (!slide || !viewport) return true;
    const s = slide.getBoundingClientRect();
    const v = viewport.getBoundingClientRect();
    return s.left >= v.left - 6 && s.right <= v.right + 6;
  }

  #scrollToIndex(index) {
    const slider = this.slider;
    if (!slider) return;
    if (typeof slider.goToIndex === 'function') {
      slider.goToIndex(index);
      return;
    }
    slider.apiS?.scrollTo?.(index);
  }
}
customElements.define('hdt-video-carousel', VideoCarousel);

/**
 * Product carousel: when Enable loop is on, always peek a card on the left.
 * Embla silently turns loop off when slides cannot fill the viewport
 * ((count - 1) * width% < 100). In that case we start on index 1 so the
 * previous card is visible; when loop stays on we keep index 0 (clone peek).
 */
function getProductCarouselStartIndex(slider, api) {
  const loopRequested = slider.getAttribute('data-pc-loop') === 'true';
  if (!loopRequested) return 0;

  const slides = api.slideNodes?.()?.length || 0;
  if (slides < 2) return 0;

  const loopActive = !!api.internalEngine?.()?.options?.loop;
  if (loopActive) return 0;

  const md = window.matchMedia('(min-width: 768px)').matches;
  const attr = md ? 'data-pc-start-index-md' : 'data-pc-start-index';
  const fromAttr = parseInt(slider.getAttribute(attr) || '1', 10);
  return Number.isFinite(fromAttr) && fromAttr > 0 ? Math.min(fromAttr, slides - 1) : 1;
}

function applyProductCarouselLeftPeek(slider, { force = false } = {}) {
  if (!slider || slider.getAttribute('data-pc-loop') !== 'true') return;
  if (!force && slider.hasAttribute('data-pc-left-ready')) return;

  const api = slider.apiS;
  if (!api) return;

  const target = getProductCarouselStartIndex(slider, api);
  if (api.selectedScrollSnap() !== target) {
    if (typeof slider.goToIndex === 'function') {
      slider.goToIndex(target, true);
    } else {
      api.scrollTo?.(target, true);
    }
  }

  slider.setAttribute('data-pc-left-ready', '');
}

function initProductCarouselLeftPeek(root = document) {
  $$4('.hdt-section__product-carousel hdt-slider-reveal[data-pc-loop="true"]', root).forEach((slider) => {
    const run = () => applyProductCarouselLeftPeek(slider);
    const runForce = () => {
      slider.removeAttribute('data-pc-left-ready');
      applyProductCarouselLeftPeek(slider, { force: true });
    };

    if (slider.apiS) run();
    else slider.addEventListener('reInit', run, { once: true });

    slider.addEventListener('reInit', () => {
      if (!slider.hasAttribute('data-pc-left-ready')) run();
    });

    if (slider.dataset.pcLeftBound) return;
    slider.dataset.pcLeftBound = '1';

    const mq = window.matchMedia('(min-width: 768px)');
    mq.addEventListener?.('change', runForce);
  });
}

initProductCarouselLeftPeek();
document.addEventListener('shopify:section:load', (event) => {
  initProductCarouselLeftPeek(event.target);
});
document.addEventListener('shopify:section:reorder', () => {
  initProductCarouselLeftPeek();
});

function variantCompareRoots(root) {
  const context = root && root.querySelectorAll ? root : document;
  const nodes = [];
  if (typeof context.matches === 'function' && context.matches('[data-variant-compare]')) {
    nodes.push(context);
  }
  context.querySelectorAll('[data-variant-compare]').forEach((node) => {
    if (!nodes.includes(node)) nodes.push(node);
  });
  return nodes;
}

function initVariantCompare(root = document) {
  variantCompareRoots(root).forEach((node) => {
    if (!node.variantCompare) node.variantCompare = new VariantCompare(node);
  });
}

function destroyVariantCompare(root) {
  variantCompareRoots(root).forEach((node) => {
    node.variantCompare?.destroy();
    delete node.variantCompare;
  });
}

class VariantCompare {
  constructor(root) {
    this.root = root;
    this.controller = new AbortController();

    let data = {};
    try {
      data = JSON.parse($4('[data-variant-compare-data]', root)?.textContent || '{}');
    } catch (error) {
      data = {};
    }

    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
    this.rows = Array.isArray(data.rows) ? data.rows : [];
    this.optionNames = Array.isArray(data.optionNames) ? data.optionNames : [];
    this.groupIndex = Number.isInteger(data.groupIndex) && data.groupIndex >= 0 && data.groupIndex < this.optionNames.length
      ? data.groupIndex
      : -1;

    const allVariants = Array.isArray(data.variants) ? data.variants : [];
    this.variants = allVariants.filter((variant) =>
      this.rows.some((row) => String(variant.specs?.[row.key] ?? '').trim() !== '')
    );
    this.byId = new Map(this.variants.map((variant) => [String(variant.id), variant]));

    this.max = clamp(Number(data.max) || 4, 2, 4);
    const defaultCount = Math.min(clamp(Number(data.defaultCount) || this.max, 2, 4), this.max);

    this.groups = this.buildGroups(Array.isArray(data.groups) ? data.groups : []);
    this.groupByKey = new Map(this.groups.map((group) => [group.key, group]));
    this.columns = this.groups
      .slice(0, defaultCount)
      .map((group) => ({ key: group.key, variantId: group.defaultId }));

    this.showDiff = false;
    this.loadingId = null;
    this.errors = {};

    this.els = {
      actions: $4('.hdt-vcompare__actions', root),
      count: $4('[data-vcompare-count]', root),
      diff: $4('[data-vcompare-diff]', root),
      clear: $4('[data-vcompare-clear]', root),
      picker: $4('[data-vcompare-picker]', root),
      pickerToggle: $4('[data-vcompare-picker-toggle]', root),
      pickerPanel: $4('[data-vcompare-picker-panel]', root),
      pickerList: $4('[data-vcompare-picker-list]', root),
      pickerNote: $4('[data-vcompare-picker-note]', root),
      need: $4('[data-vcompare-need]', root),
      empty: $4('[data-vcompare-empty]', root),
      note: $4('[data-vcompare-note]', root),
      scroll: $4('[data-vcompare-scroll]', root),
      head: $4('[data-vcompare-head]', root),
      body: $4('[data-vcompare-body]', root)
    };

    this.buildPicker();
    this.bind();
    this.render();
  }

  destroy() {
    this.controller.abort();
  }

  text(name, replacements = {}) {
    let value = this.root.dataset[name] || '';
    Object.entries(replacements).forEach(([token, replacement]) => {
      value = value.replace(`[${token}]`, replacement);
    });
    return value;
  }

  buildGroups(order) {
    const map = new Map();
    this.variants.forEach((variant) => {
      const key = this.groupIndex >= 0 ? String(variant.options?.[this.groupIndex] ?? '') : String(variant.id);
      if (!map.has(key)) {
        map.set(key, { key, label: this.groupIndex >= 0 ? key : variant.title, variants: [] });
      }
      map.get(key).variants.push(variant);
    });

    const groups = [...map.values()];
    if (this.groupIndex >= 0 && order.length) {
      const position = (key) => {
        const index = order.indexOf(key);
        return index === -1 ? order.length : index;
      };
      groups.sort((a, b) => position(a.key) - position(b.key));
    }

    groups.forEach((group) => {
      const preferred = group.variants.find((variant) => variant.available) || group.variants[0];
      group.defaultId = String(preferred.id);
    });
    return groups;
  }

  subLabel(variant) {
    if (this.groupIndex < 0 || !Array.isArray(variant.options)) return '';
    return variant.options.filter((_, index) => index !== this.groupIndex).join(' / ');
  }

  get subOptionName() {
    return this.optionNames.filter((_, index) => index !== this.groupIndex).join(' / ');
  }

  get isMissing() {
    return this.rows.length === 0 || this.variants.length === 0;
  }

  createEl(tag, className, text) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined) el.textContent = text;
    return el;
  }

  createIcon(viewBox, d, className) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', viewBox);
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    if (className) svg.setAttribute('class', className);
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', d);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.5');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.append(path);
    return svg;
  }

  buildPicker() {
    const { pickerList } = this.els;
    if (!pickerList) return;
    pickerList.replaceChildren();
    this.groups.forEach((group) => {
      const label = this.createEl('label', 'hdt-vcompare__picker-item');
      const input = this.createEl('input');
      input.type = 'checkbox';
      input.value = group.key;
      input.dataset.vcompareToggle = '';
      label.append(input, this.createEl('span', '', group.label));
      pickerList.append(label);
    });
  }

  bind() {
    const { signal } = this.controller;
    const { pickerToggle, pickerPanel, picker, clear } = this.els;

    pickerToggle?.addEventListener('click', () => this.setPicker(pickerPanel.hidden), { signal });

    document.addEventListener('click', (event) => {
      if (pickerPanel && !pickerPanel.hidden && picker && !picker.contains(event.target)) this.setPicker(false);
    }, { signal });

    this.root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && pickerPanel && !pickerPanel.hidden) {
        this.setPicker(false);
        pickerToggle?.focus();
      }
    }, { signal });

    clear?.addEventListener('click', () => {
      this.columns = [];
      this.showDiff = false;
      if (this.els.diff) this.els.diff.checked = false;
      this.render();
    }, { signal });

    this.root.addEventListener('change', (event) => {
      const target = event.target;

      if (target.matches('[data-vcompare-toggle]')) {
        const key = target.value;
        const index = this.columns.findIndex((column) => column.key === key);
        if (target.checked && index === -1 && this.columns.length < this.max) {
          const group = this.groupByKey.get(key);
          if (group) this.columns.push({ key, variantId: group.defaultId });
        } else if (!target.checked && index !== -1) {
          this.columns.splice(index, 1);
        }
        this.render();
        return;
      }

      if (target.matches('[data-vcompare-variant]')) {
        const column = this.columns[Number(target.dataset.vcompareVariant)];
        if (column && this.byId.has(target.value)) {
          column.variantId = target.value;
          this.render();
        }
        return;
      }

      if (target.matches('[data-vcompare-diff]')) {
        this.showDiff = target.checked;
        this.render();
      }
    }, { signal });

    this.root.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-vcompare-remove]');
      if (remove) {
        this.columns.splice(Number(remove.dataset.vcompareRemove), 1);
        this.render();
        return;
      }

      const atc = event.target.closest('[data-vcompare-atc]');
      if (atc && atc.getAttribute('aria-disabled') !== 'true') {
        this.addToCart(atc.dataset.vcompareAtc);
      }
    }, { signal });
  }

  setPicker(open) {
    const { pickerPanel, pickerToggle } = this.els;
    if (!pickerPanel) return;
    pickerPanel.hidden = !open;
    pickerToggle?.setAttribute('aria-expanded', String(open));
  }

  columnVariant(column) {
    return this.byId.get(String(column.variantId)) || null;
  }

  renderPicker() {
    const { pickerList, pickerNote } = this.els;
    const selected = new Set(this.columns.map((column) => column.key));
    const atMax = this.columns.length >= this.max;
    pickerList?.querySelectorAll('[data-vcompare-toggle]').forEach((input) => {
      input.checked = selected.has(input.value);
      input.disabled = !input.checked && atMax;
      input.closest('label')?.classList.toggle('is-disabled', input.disabled);
    });
    if (pickerNote) {
      pickerNote.hidden = !atMax || this.groups.length <= this.max;
      pickerNote.textContent = this.text('maxText', { max: String(this.max) });
    }
  }

  renderHeadCell(column, index) {
    const group = this.groupByKey.get(column.key);
    const variant = this.columnVariant(column);
    const th = this.createEl('th', 'hdt-vcompare__cell hdt-vcompare__head-cell hdt-vcompare__col');
    th.scope = 'col';
    if (!group || !variant) return th;

    const card = this.createEl('div', 'hdt-vcompare__card');

    const remove = this.createEl('button', 'hdt-vcompare__remove');
    remove.type = 'button';
    remove.dataset.vcompareRemove = String(index);
    remove.setAttribute('aria-label', this.text('removeLabel', { title: group.label }));
    remove.append(this.createIcon('0 0 14 14', 'M2 2l10 10M12 2 2 12'));
    card.append(remove);

    if (variant.image) {
      const media = this.createEl('a', 'hdt-vcompare__media');
      media.href = variant.url || '#';
      media.tabIndex = -1;
      media.setAttribute('aria-hidden', 'true');
      const img = this.createEl('img');
      img.src = variant.image;
      img.alt = '';
      img.width = 144;
      img.height = 112;
      img.loading = 'lazy';
      media.append(img);
      card.append(media);
    }

    const info = this.createEl('div', 'hdt-vcompare__info');
    const title = this.createEl('a', 'hdt-vcompare__title', group.label);
    title.href = variant.url || '#';
    info.append(title);

    if (this.groupIndex >= 0 && group.variants.length > 1) {
      const picker = this.createEl('label', 'hdt-vcompare__variant');
      const select = this.createEl('select', 'hdt-vcompare__select');
      select.dataset.vcompareVariant = String(index);
      select.setAttribute('aria-label', this.text('chooseLabel', { option: this.subOptionName }));
      group.variants.forEach((item) => {
        const label = this.subLabel(item) || item.title;
        const option = this.createEl('option', '', item.available ? label : `${label} — ${this.text('soldOut')}`);
        option.value = String(item.id);
        option.selected = String(item.id) === String(variant.id);
        select.append(option);
      });
      picker.append(select, this.createIcon('0 0 12 8', 'M1 1.5 6 6.5 11 1.5', 'hdt-vcompare__chevron'));
      info.append(picker);
    } else if (this.subLabel(variant)) {
      info.append(this.createEl('p', 'hdt-vcompare__sub', this.subLabel(variant)));
    }

    card.append(info);
    th.append(card);
    return th;
  }

  renderPriceRow() {
    const tr = this.createEl('tr', 'hdt-vcompare__buy-row');
    const label = this.createEl('th', 'hdt-vcompare__cell hdt-vcompare__label', this.text('priceLabel'));
    label.scope = 'row';
    tr.append(label);

    this.columns.forEach((column) => {
      const variant = this.columnVariant(column);
      const td = this.createEl('td', 'hdt-vcompare__cell hdt-vcompare__buy');
      if (!variant) {
        tr.append(td);
        return;
      }

      const price = this.createEl('div', 'hdt-vcompare__price');
      price.append(this.createEl('span', 'hdt-vcompare__price-sale', variant.price || ''));
      if (variant.compareAt) price.append(this.createEl('s', 'hdt-vcompare__price-compare', variant.compareAt));
      td.append(price);

      const id = String(variant.id);
      const busy = this.loadingId === id;
      const button = this.createEl('button', 'hdt-btn hdt-btn-solid hdt-btn--size-s hdt-vcompare__atc');
      button.type = 'button';
      button.dataset.vcompareAtc = id;
      button.textContent = variant.available ? this.text('addToCart') : this.text('soldOut');
      if (!variant.available || this.loadingId) button.setAttribute('aria-disabled', 'true');
      if (!variant.available) button.disabled = true;
      if (busy) button.setAttribute('aria-busy', 'true');
      td.append(button);

      if (this.errors[id]) {
        const error = this.createEl('p', 'hdt-vcompare__error', this.errors[id]);
        error.setAttribute('role', 'alert');
        td.append(error);
      }
      tr.append(td);
    });
    return tr;
  }

  renderSpecRows() {
    let visible = 0;
    this.rows.forEach((row) => {
      const values = this.columns.map((column) => {
        const variant = this.columnVariant(column);
        return String(variant?.specs?.[row.key] ?? '').trim();
      });
      const differs = values.length > 1 && values.some((value) => value !== values[0]);
      if (this.showDiff && !differs) return;
      visible += 1;

      const tr = this.createEl('tr');
      const label = this.createEl('th', `hdt-vcompare__cell hdt-vcompare__label${differs ? ' is-diff' : ''}`);
      label.scope = 'row';
      const inner = this.createEl('div', 'hdt-vcompare__label-inner');
      inner.append(this.createEl('span', '', row.label || row.key));
      if (differs) inner.append(this.createEl('span', 'hdt-vcompare__badge', this.text('differentLabel')));
      label.append(inner);
      tr.append(label);

      values.forEach((value) => {
        tr.append(this.createEl('td', `hdt-vcompare__cell hdt-vcompare__value${differs ? ' is-diff' : ''}`, value || '—'));
      });
      this.els.body.append(tr);
    });
    return visible;
  }

  render() {
    const { actions, count, need, empty, note, scroll, head, body } = this.els;
    const missing = this.isMissing;
    const total = this.columns.length;

    const active = document.activeElement;
    let restoreSelector = '';
    if (active && this.root.contains(active)) {
      if (active.dataset.vcompareVariant !== undefined) {
        restoreSelector = `[data-vcompare-variant="${active.dataset.vcompareVariant}"]`;
      } else if (active.dataset.vcompareAtc) {
        restoreSelector = `[data-vcompare-atc="${active.dataset.vcompareAtc}"]`;
      }
    }

    if (actions) actions.hidden = missing;
    if (count) {
      count.hidden = total === 0;
      count.textContent = this.text(total === 1 ? 'countLabelOne' : 'countLabel', { count: String(total) });
    }
    if (need) {
      need.hidden = missing || total !== 1;
      need.textContent = this.text('needTwo');
    }
    if (empty) {
      empty.hidden = !missing && total > 0;
      if (this.rows.length === 0) empty.textContent = this.text('missingRows');
      else if (this.variants.length === 0) empty.textContent = this.text('missingText');
      else empty.textContent = this.text('emptyText');
    }
    if (scroll) scroll.hidden = missing || total === 0;

    head?.replaceChildren();
    body?.replaceChildren();
    this.renderPicker();

    let visibleRows = 0;
    if (!missing && total > 0 && head && body) {
      const corner = this.createEl('th', 'hdt-vcompare__cell hdt-vcompare__head-cell hdt-vcompare__label');
      corner.scope = 'col';
      head.append(corner);
      this.columns.forEach((column, index) => head.append(this.renderHeadCell(column, index)));
      body.append(this.renderPriceRow());
      visibleRows = this.renderSpecRows();
    }

    if (note) {
      const showNote = !missing && total > 1 && this.showDiff && visibleRows === 0;
      note.hidden = !showNote;
      note.textContent = showNote ? this.text('matchText') : '';
    }

    if (restoreSelector) $4(restoreSelector, this.root)?.focus();
  }

  /* The core cart drawer only reacts to product-form / product-bundle sources,
     so the "after add to cart" theme setting is applied here. */
  afterAddToCart(action) {
    const cartDrawer = $4('cart-drawer');
    if (action === 'open_cart_drawer') {
      cartDrawer?.open?.();
      return;
    }
    if (cartDrawer || window.themeHDN?.settings?.pageType === 'cart' || !action.startsWith('go_')) return;
    window.location.href = action === 'go_custom_link'
      ? window.themeHDN.settings.cartCustomLink
      : `${Shopify.routes.root}${action === 'go_cart_page' ? 'cart' : 'checkout'}`;
  }

  async addToCart(variantId) {
    if (this.loadingId) return;
    const cart = $4('[ref="hdt-cart"]');
    this.loadingId = String(variantId);
    this.errors[this.loadingId] = '';
    this.render();
    document.dispatchEvent(new CustomEvent(loadingStart));

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: cart?.sectionsToRender?.map((section) => section.id) ?? ['cart-json']
        })
      });
      const data = await response.json();

      if (data.status) {
        this.errors[this.loadingId] = data.description || data.message || this.text('cartError');
        document.dispatchEvent(new CustomEvent(cartReload, { bubbles: true, detail: { variantId: this.loadingId } }));
      } else {
        const cartData = {
          ...data,
          ...JSON.parse(data?.sections?.['cart-json']?.split('[split_94]')?.[1] || '{}')
        };
        const actionAfterATC = window.themeHDN?.settings?.actionAfterATC || 'open_cart_drawer';
        this.root.dispatchEvent(new CustomEvent(cartUpdate, {
          bubbles: true,
          detail: {
            source: 'variant-compare',
            actionAfterATC,
            variantId: this.loadingId,
            cartData
          }
        }));
        this.afterAddToCart(actionAfterATC);
      }
    } catch (error) {
      console.error(error);
      this.errors[this.loadingId] = this.text('cartError');
    } finally {
      document.dispatchEvent(new CustomEvent(loadingEnd));
      this.loadingId = null;
      this.render();
    }
  }
}

initVariantCompare();
document.addEventListener('shopify:section:load', (event) => {
  initVariantCompare(event.target);
});
document.addEventListener('shopify:section:unload', (event) => {
  destroyVariantCompare(event.target);
});

document.addEventListener('click', (event) => {
  const link = event.target.closest?.('[data-compare-scroll]');
  if (!link) return;

  const href = link.getAttribute('href') || '';
  if (!href.startsWith('#')) return;

  const target = document.getElementById(href.slice(1));
  if (!target) return;

  event.preventDefault();
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  history.replaceState(null, '', href);
});

function syncStickyCompareLinks() {
  document.querySelectorAll('[data-compare-requires-section]').forEach((link) => {
    const href = link.getAttribute('href') || '';
    const id = href.startsWith('#') ? href.slice(1) : '';
    const hasSection =
      (id && document.getElementById(id)) ||
      document.querySelector('[data-variant-compare]');
    link.toggleAttribute('hidden', !hasSection);
  });
}

syncStickyCompareLinks();
document.addEventListener('shopify:section:load', () => syncStickyCompareLinks());
document.addEventListener('shopify:section:unload', () => {
  requestAnimationFrame(() => syncStickyCompareLinks());
});
document.addEventListener('shopify:section:reorder', () => syncStickyCompareLinks());

function parseJsonOnce(node, cacheKey, source) {
  if (!node[cacheKey]) {
    try {
      node[cacheKey] = JSON.parse(source || '{}') || {};
    } catch (error) {
      node[cacheKey] = {};
    }
  }
  return node[cacheKey];
}

function syncProductSpecGroups(context = document) {
  const scope = context && context.querySelectorAll ? context : document;
  scope.querySelectorAll('.hdt-product-spec-group').forEach((group) => {
    const items = group.querySelectorAll('.hdt-product-spec-item');
    if (!items.length) return;
    group.hidden = Array.from(items).every((item) => item.hidden);
  });
}

function applyFireplaceMedia(root, variantId) {
  const script = root.querySelector('[data-fireplace-media]');
  const image = root.querySelector('img.hdt-product-details__img');
  if (!script || !image) return;

  const media = parseJsonOnce(root, '_fireplaceMedia', script.textContent);
  const variants = media.variants || {};
  if (!Object.prototype.hasOwnProperty.call(variants, variantId)) return;

  const variantEntry = variants[variantId];
  const entry = variantEntry || media.default;
  if (!entry || !entry.src || image.dataset.fireplaceSrc === entry.src) return;

  image.dataset.fireplaceSrc = entry.src;
  image.srcset = entry.srcset || '';
  image.src = entry.src;
  if (entry.width) image.width = entry.width;
  if (entry.height) image.height = entry.height;
  image.alt = entry.alt || '';

  const source = root.querySelector('source[data-default-srcset]');
  if (source) source.srcset = variantEntry ? entry.srcset : source.dataset.defaultSrcset;
}

function applyVariantSpecs(variantId) {
  if (!variantId) return;
  const id = String(variantId);
  let changed = false;

  document.querySelectorAll('[data-spec-values]').forEach((item) => {
    const values = parseJsonOnce(item, '_specValues', item.dataset.specValues);
    if (!Object.prototype.hasOwnProperty.call(values, id)) return;

    const text = values[id] || '';
    const valueNode = item.querySelector('[data-spec-value]');
    if (valueNode && valueNode.textContent !== text) valueNode.textContent = text;
    item.hidden = text === '';
    changed = true;
  });

  if (changed) syncProductSpecGroups();
  document.querySelectorAll('[data-fireplace-specs]').forEach((root) => applyFireplaceMedia(root, id));
}

syncProductSpecGroups();
document.addEventListener('variant-change', (event) => {
  applyVariantSpecs(event.detail?.variant?.id);
});
document.addEventListener('hdt:updateUrl', () => {
  applyVariantSpecs(new URLSearchParams(window.location.search).get('variant'));
});
document.addEventListener('shopify:section:load', (event) => {
  syncProductSpecGroups(event.target);
});
